import { useElementDistribution } from '../../hooks/useElementDistribution';
import {
  searchReportingFetch as fetch,
  searchReportingLegacyUrl,
} from '../../lib/flags/search-reporting';
import { publicationStateForSave } from '../../utils/formPublication';
import { useOnboardingDriver } from '../onboarding/GuidedFormCreation/OnboardingDriverContext';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/ui/section-header';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { FormInput } from '../../components/ui/form-input';
import { FormSelect } from '../../components/ui/form-select';
import { PagePathInput } from '@/components/ui/page-path-input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info, ChevronDown, X } from 'lucide-react';
import ElementEditorSection, {
  elementEditorSectionHasErrors,
  elementEditorSectionIcons,
  focusFirstInvalidElement,
} from '../workspaces/ElementEditorSection';
import { getAuthToken, getUserEmail } from '../../utils/auth';
import { toast } from 'sonner';
import { copyToClipboard } from '@charitystack/shared';
import { useLatestCallback } from '../../hooks/useLatestCallback';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { getCountryOptions } from '@charitystack/shared';
// @ts-expect-error - JS file without type definitions
import { queryContactsTopContacts } from '../../utils/RocksetQueries/ContactsQueries';
import type { FormData as BaseFormData } from './tabs/types';
import ConfirmationModal from '../modals/ConfirmationModal';
import MultiSelectDropdownNoCreate from '../dropdowns/MultiSelectDropdownNoCreate';
import OverlaySnippets from './OverlaySnippets';
import { buildOverlayElementURL } from '../../utils/urlElement';
import {
  findURLBuilderTicketByName,
  getURLBuilderConfiguredSeatCount,
  getURLBuilderSavedSeatCount,
  restoreURLBuilderSeatAttendees,
  shouldPersistURLBuilderTicketSeats,
} from '../../utils/urlBuilderTicketPrefill';

// Ticket info from event forms
interface TicketInfo {
  id: string;
  name: string;
  price: number | string;
  seats?: number;
  groupSize?: number;
  type?: 'SINGLE' | 'GROUP';
  ticketType?: 'INDIVIDUAL' | 'GROUP';
  groupSizeMode?: 'FIXED' | 'MINIMUM';
  minimumSeats?: number;
  maximumSeats?: number | null;
  seatCapacity?: number;
  seatsSold?: number;
  quantity?: number;
  sold?: number;
}

// Attendee info for a single seat (includes per-seat custom input values)
interface SeatAttendee {
  firstName: string;
  lastName: string;
  email: string;
  customInputs: Record<string, string>;
}

// Crowdfunding giving level option
interface CrowdfundingOption {
  id: string;
  title: string;
  amount: string;
  description: string;
}

// Scheduled payment range config per frequency (mirrors CreateFundraiserDrawer.types.ts)
type DateConfigType = 'none' | 'fixed' | 'dynamic';

interface ScheduledRangeConfig {
  enabled: boolean;
  scheduleType: string;
  startDateOffset: number | null;
  endDateOffset: number | null;
  scheduledTime?: string;
  startDateType?: DateConfigType;
  endDateType?: DateConfigType;
  fixedStartDate?: string | null;
  fixedEndDate?: string | null;
  lockedScheduleDate?: boolean;
}

interface FrequencyScheduledRangeMap {
  Daily?: ScheduledRangeConfig;
  Weekly?: ScheduledRangeConfig;
  Monthly?: ScheduledRangeConfig;
  Annually?: ScheduledRangeConfig;
}

type RecurringFrequency = 'Daily' | 'Weekly' | 'Monthly' | 'Annually';

// Extended form data with formType and configuration for dynamic fields
interface AvailableFormData extends BaseFormData {
  active?: boolean;
  formType?: string;
  fundraiserType?: string; // 'EMBED_FORM' | 'CROWDFUNDING_FORM' | 'PRODUCT'
  funds?: string[];
  frequencies?: string[];
  customInputs?: FormCustomInput[];
  productGroups?: {
    productGroupName: string;
    products: { productID: string; productName: string; productPrice: number }[];
  }[];
  tickets?: TicketInfo[];
  lightColor?: string;
  // Per-frequency crowdfunding options
  oneTimeCrowdfundingOptions?: CrowdfundingOption[];
  dailyCrowdfundingOptions?: CrowdfundingOption[];
  weeklyCrowdfundingOptions?: CrowdfundingOption[];
  monthlyCrowdfundingOptions?: CrowdfundingOption[];
  annuallyCrowdfundingOptions?: CrowdfundingOption[];
  crowdfundingOptions?: CrowdfundingOption[]; // legacy
  // Scheduled payments
  requireScheduleDonations?: boolean;
  defaultScheduledRange?: FrequencyScheduledRangeMap;
}

// Custom input from form config (simplified for URL builder)
interface FormCustomInput {
  id: string;
  title: string;
  type:
    | 'SHORT_TEXT_INPUT'
    | 'LONG_TEXT_INPUT'
    | 'SINGLE_SELECTION_DROPDOWN'
    | 'MULTI_SELECTION_DROPDOWN';
  required: boolean;
  options?: string[];
  questionType?: string;
  inputType?: string;
  global?: boolean;
}

// Types
// CON-167. This is a local structural description of the `merchant` prop, whose
// real type is `Merchant` (src/types/common.ts). Two fields had drifted from it:
//
//   * `organizationId` was read at line ~689 (it becomes CSMerchantID on the
//     element payload) but never declared here — the same field
//     utils/paymentReadiness.d.ts already declares on a merchant record.
//   * `globalCustomInputs` was declared as FormCustomInput[] while `Merchant`
//     supplies `unknown[]`, which is why passing a real merchant in from
//     CreateDropdown failed. The declaration is now the truth about what
//     arrives; the narrowing to FormCustomInput[] stays exactly where it
//     already was — the deliberate cast at line ~1030 — rather than being
//     asserted up here where nothing has checked it.
interface MerchantData {
  merchantName: string;
  id: string;
  slug?: string;
  organizationId?: string;
  organizationBaseURLs?: string[];
  globalCustomInputs?: unknown[];
}

interface URLElementData {
  elementID: string;
  elementName: string;
  destinationType?: DestinationType;
  formIDs?: string[];
  formNames?: string[];
  prefillParams?: Record<string, string>;
  overlayPath?: string;
  contactID?: string;
  active: boolean;
}

interface URLBuilderDrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  merchant?: MerchantData;
  elementData?: URLElementData;
  onSuccess?: (elementData: any) => void;
  user?: { email?: string; given_name?: string };
  groups?: string[];
  presentation?: 'drawer' | 'workspace';
  workspaceMode?: 'create' | 'edit';
  onWorkspaceBack?: () => void;
  setWorkspaceActions?: (actions: WorkspaceHeaderAction[] | null) => void;
}

interface WorkspaceHeaderAction {
  key: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'ghost' | 'outline' | 'neutral' | 'primary' | 'danger';
  className?: string;
}

type DestinationType =
  | 'HOSTED_PAGE_DONATE'
  | 'HOSTED_PAGE_ATTEND'
  | 'OVERLAY_DONATE'
  | 'OVERLAY_ATTEND';

interface URLFormData {
  elementName: string;
  destinationType: DestinationType;
  connectedForms: string[];
  formIDs: string[];
  prefillAmount: string;
  prefillFund: string;
  prefillFrequency: string;
  prefillFirstname: string;
  prefillLastname: string;
  prefillOrganizationname: string;
  prefillEmail: string;
  prefillPhone: string;
  prefillAddress: string;
  prefillAddress2: string;
  prefillCity: string;
  prefillState: string;
  prefillZip: string;
  prefillCountry: string;
  overlayPath: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  utmContent: string;
}

interface Tab {
  name: string;
  step: string;
  current: boolean;
}

function classNames(...classes: string[]): string {
  return classes.filter(Boolean).join(' ');
}

// Normalize a title for use as a URL param name:
// lowercase, spaces → underscores, strip non-alphanumeric/underscore chars (e.g., # & @)
// This prevents URL-unsafe characters from breaking param forwarding.
const normalizeParamTitle = (title: string): string =>
  title
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

// Build a collision-free map from custom input ID → URL param name.
// Form-level inputs always use `ci_<title>`. If a global input's title collides
// with a form-level input, the global uses `gci_<title>` instead.
function buildParamNameMap(
  formInputs: FormCustomInput[],
  globalInputs: FormCustomInput[],
): Map<string, string> {
  const map = new Map<string, string>();
  const formParamNames = new Set<string>();

  // Form-level inputs get priority with `ci_` prefix
  formInputs.forEach((input) => {
    const paramName = input.title ? `ci_${normalizeParamTitle(input.title)}` : `ci_${input.id}`;
    map.set(input.id, paramName);
    formParamNames.add(paramName);
  });

  // Global inputs use `ci_` unless it collides with a form-level input
  globalInputs.forEach((input) => {
    const baseParamName = input.title ? `ci_${normalizeParamTitle(input.title)}` : `ci_${input.id}`;
    const paramName = formParamNames.has(baseParamName)
      ? `gci_${normalizeParamTitle(input.title || '') || input.id}`
      : baseParamName;
    map.set(input.id, paramName);
  });

  return map;
}

const tabs: Tab[] = [
  { name: 'Configuration', step: 'configuration', current: true },
  { name: 'Custom Inputs', step: 'custom-inputs', current: false },
  { name: 'Contact Info', step: 'contact-info', current: false },
  { name: 'UTM Parameters', step: 'utm', current: false },
];

const initialFormData: URLFormData = {
  elementName: '',
  destinationType: 'HOSTED_PAGE_DONATE',
  connectedForms: [],
  formIDs: [],
  prefillAmount: '',
  prefillFund: '',
  prefillFrequency: '',
  prefillFirstname: '',
  prefillLastname: '',
  prefillOrganizationname: '',
  prefillEmail: '',
  prefillPhone: '',
  prefillAddress: '',
  prefillAddress2: '',
  prefillCity: '',
  prefillState: '',
  prefillZip: '',
  prefillCountry: '',
  overlayPath: '',
  utmSource: '',
  utmMedium: '',
  utmCampaign: '',
  utmTerm: '',
  utmContent: '',
};

const DESTINATION_TYPE_OPTIONS: { value: 'HOSTED_PAGE' | 'OVERLAY'; label: string }[] = [
  { value: 'HOSTED_PAGE', label: 'Hosted Page' },
  { value: 'OVERLAY', label: 'Overlay' },
];

const FORM_TYPE_OPTIONS: { value: 'DONATE' | 'ATTEND'; label: string }[] = [
  { value: 'DONATE', label: 'Fundraiser' },
  { value: 'ATTEND', label: 'Event' },
];

const FREQUENCY_OPTIONS = [
  { value: '', label: '' },
  { value: 'ONE_TIME', label: 'One-Time' },
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'ANNUALLY', label: 'Annually' },
];

// Map backend frequency values to recurring display labels (for schedule range keys)
const BACKEND_TO_DISPLAY_FREQ: Record<string, RecurringFrequency> = {
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  ANNUALLY: 'Annually',
};

// Default schedule config for a frequency
const makeDefaultScheduleConfig = (): ScheduledRangeConfig => ({
  enabled: false,
  scheduleType: 'immediate',
  startDateOffset: null,
  endDateOffset: null,
  scheduledTime: '',
  startDateType: 'none',
  endDateType: 'none',
  fixedStartDate: null,
  fixedEndDate: null,
  lockedScheduleDate: false,
});

// Normalize backend defaultScheduledRange (handles legacy flat format + new per-frequency format)
const normalizeScheduledRange = (
  data: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  recurringFreqs: RecurringFrequency[],
): FrequencyScheduledRangeMap => {
  if (!data) {
    const result: FrequencyScheduledRangeMap = {};
    for (const f of recurringFreqs) result[f] = makeDefaultScheduleConfig();
    return result;
  }

  // New format: already has frequency keys
  if (data.Daily || data.Weekly || data.Monthly || data.Annually) {
    const legacyGlobalLock = data.lockedScheduleDate;
    const result: FrequencyScheduledRangeMap = {};
    for (const f of recurringFreqs) {
      result[f] = data[f]
        ? {
            ...makeDefaultScheduleConfig(),
            ...data[f],
            lockedScheduleDate: data[f].lockedScheduleDate ?? legacyGlobalLock ?? false,
          }
        : makeDefaultScheduleConfig();
    }
    return result;
  }

  // Legacy flat format: apply same config to all recurring frequencies
  if (data.enabled !== undefined || data.scheduleType !== undefined) {
    const legacyConfig: ScheduledRangeConfig = {
      ...makeDefaultScheduleConfig(),
      enabled: data.enabled ?? false,
      scheduleType: data.scheduleType || 'immediate',
      startDateOffset: data.startDateOffset ?? null,
      endDateOffset: data.endDateOffset ?? null,
      scheduledTime: data.scheduledTime || '',
    };
    const result: FrequencyScheduledRangeMap = {};
    for (const f of recurringFreqs) result[f] = { ...legacyConfig };
    return result;
  }

  // Fallback
  const result: FrequencyScheduledRangeMap = {};
  for (const f of recurringFreqs) result[f] = makeDefaultScheduleConfig();
  return result;
};

export default function URLBuilderDrawer(props: URLBuilderDrawerProps) {
  const paymentsReady = useOnboardingDriver()?.chargesEnabled === true;
  const history = useHistory();
  const useCreateWorkspaceCards = props.presentation === 'workspace';
  const [formData, setFormData] = useState<URLFormData>(initialFormData);
  const [elementID, setElementID] = useState<string | null>(null);
  const [availableForms, setAvailableForms] = useState<AvailableFormData[]>([]);
  const [formsLoading, setFormsLoading] = useState<boolean>(false);
  const [active, setActive] = useState<boolean>(true);
  const { canDistribute, refresh: verifyDistribution } = useElementDistribution(
    {
      ...props.elementData,
      elementID,
      active,
      elementType: 'ELEMENT_URL',
      formIDs: [
        ...(props.elementData?.formIDs || []),
        ...formData.connectedForms
          .map((name) => availableForms.find((form) => form.formName === name)?.formID)
          .filter((id): id is string => !!id),
      ],
    },
    paymentsReady,
    props.open,
  );
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const [currentStep, setCurrentStep] = useState<string>('configuration');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isActivating, setIsActivating] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

  const [customInputValues, setCustomInputValues] = useState<Record<string, string>>({});
  const [selectedSponsorshipGroup, setSelectedSponsorshipGroup] = useState<string>('');
  const [selectedSponsorshipProduct, setSelectedSponsorshipProduct] = useState<string>('');

  const [crowdfundingDropdownOpen, setCrowdfundingDropdownOpen] = useState<boolean>(false);
  const crowdfundingDropdownRef = useRef<HTMLDivElement>(null);

  const [selectedTicketId, setSelectedTicketId] = useState<string>('');
  const [seatAttendees, setSeatAttendees] = useState<SeatAttendee[]>([]);

  // Scheduled payment override state
  const [scheduleRanges, setScheduleRanges] = useState<FrequencyScheduledRangeMap>({});
  const [activeScheduleTab, setActiveScheduleTab] = useState<string>('');
  const scheduleInitializedForFormRef = useRef<string | null>(null);

  // Contact search state
  const [contactSearchTerm, setContactSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [contactSearchResults, setContactSearchResults] = useState<any[]>([]);
  const [isSearchingContacts, setIsSearchingContacts] = useState(false);
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const contactSearchRef = useRef<HTMLDivElement>(null);

  const formRef = useRef<HTMLFormElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const restoredCustomInputIdsRef = useRef<Set<string>>(new Set());
  const touchedCustomInputIdsRef = useRef<Set<string>>(new Set());

  // Country list for dropdown (frozen canonical options, PE-1878)
  const countries = useMemo(() => getCountryOptions(), []);

  const updateFormData = (field: keyof URLFormData, value: any): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetCustomInputRestoreState = () => {
    restoredCustomInputIdsRef.current.clear();
    touchedCustomInputIdsRef.current.clear();
  };

  const updateCustomInputValue = (inputId: string, value: string) => {
    touchedCustomInputIdsRef.current.add(inputId);
    setCustomInputValues((prev) => ({
      ...prev,
      [inputId]: value,
    }));
  };

  const handleFieldFocus = (fieldName: string) => {
    setInvalidFields((prev) => {
      const newInvalidFields = new Set(prev);
      newInvalidFields.delete(fieldName);
      const nonPrefixedName = fieldName.includes('-')
        ? fieldName.split('-').slice(1).join('-')
        : fieldName;
      newInvalidFields.delete(nonPrefixedName);
      return newInvalidFields;
    });
  };

  const isFieldInvalid = (fieldName: string) => {
    if (!invalidFields) return false;
    if (invalidFields.has(fieldName)) return true;
    for (const tab of tabs) {
      if (invalidFields.has(`${tab.step}-${fieldName}`)) return true;
    }
    return false;
  };

  // Get merchant domain for overlay URLs (guarded — only if configured)
  const getMerchantDomain = (): string => {
    const baseUrl = props.merchant?.organizationBaseURLs?.[0];
    if (!baseUrl) return '';
    // Ensure it has a protocol
    const normalized = baseUrl.startsWith('http') ? baseUrl : `https://${baseUrl}`;
    // Remove trailing slash
    return normalized.replace(/\/+$/, '');
  };

  // Generate the URL based on form data.
  // Uses elementID-only format: ?elementid={id} — all params fetched dynamically at runtime.
  // Accepts an optional explicit eid for cases where elementID state hasn't been set yet (e.g., after create).
  const generateURLWithElementID = (eid: string): string => {
    const formID = formData.formIDs[0] || '';
    if (!formID || !eid) return '';

    const baseUrl =
      import.meta.env.VITE_APP_ENV === 'development'
        ? 'https://dev.dy88r2gkjfu67.amplifyapp.com'
        : import.meta.env.VITE_APP_ENV === 'staging'
          ? 'https://staging.charitystack.com'
          : 'https://charitystack.com';

    switch (formData.destinationType) {
      case 'HOSTED_PAGE_DONATE':
        return `${baseUrl}/donate/${formID}?elementid=${eid}`;
      case 'HOSTED_PAGE_ATTEND':
        return `${baseUrl}/attend/${formID}?elementid=${eid}`;
      case 'OVERLAY_DONATE':
      case 'OVERLAY_ATTEND': {
        const triggerParam = formData.destinationType === 'OVERLAY_ATTEND' ? 'attend' : 'donate';
        // Single source of truth shared with OverlaySnippets so the saved URL
        // always matches the snippet merchants copy/paste.
        return buildOverlayElementURL({
          domain: getMerchantDomain(),
          pagePath: formData.overlayPath,
          type: triggerParam,
          formID,
          elementID: eid || undefined,
        });
      }
      default:
        return '';
    }
  };

  const generateURL = (): string => generateURLWithElementID(elementID || '');

  // Handle form submission
  const handleSubmit = async (): Promise<void> => {
    const newInvalidFields = new Set<string>();

    if (!formData.elementName.trim()) {
      newInvalidFields.add('configuration-elementName');
    }
    if (!formData.connectedForms || formData.connectedForms.length === 0) {
      newInvalidFields.add('configuration-connectedForms');
    }
    const isOverlay =
      formData.destinationType === 'OVERLAY_DONATE' ||
      formData.destinationType === 'OVERLAY_ATTEND';
    if (isOverlay && !getMerchantDomain()) {
      newInvalidFields.add('configuration-destinationType');
      toast.error('Overlay requires a Website URL configured in Settings → Organization', {
        action: {
          label: 'Open Settings',
          onClick: () => {
            props.setOpen(false);
            history.push('/settings');
          },
        },
      });
    }

    // Reject obviously-bad prefill amounts at save time so we never store
    // values like "abc", "5abc", "-5", or "0" that the form layer would
    // silently discard at runtime. Number() (not parseFloat) so leading-
    // numeric garbage like "5abc" becomes NaN and is rejected. Empty stays
    // valid (no amount prefill).
    if (formData.prefillAmount.trim() !== '') {
      const parsedAmount = Number(formData.prefillAmount);
      const amountIsValid =
        Number.isFinite(parsedAmount) && parsedAmount > 0 && parsedAmount <= 1_000_000;
      if (!amountIsValid) {
        newInvalidFields.add('configuration-prefillAmount');
        toast.error('Prefill amount must be a positive number up to $1,000,000');
      }
    }

    if (newInvalidFields.size > 0) {
      setInvalidFields(newInvalidFields);
      setCurrentStep('configuration');
      toast.error('Please fix validation errors');
      focusFirstInvalidElement(rightPanelRef.current);
      return;
    }

    setIsSaving(true);

    try {
      // Build prefill params object (only non-empty values)
      const prefillParams: Record<string, string> = {};
      const prefillFields: [keyof URLFormData, string][] = [
        ['prefillAmount', 'amount'],
        ['prefillFund', 'fund'],
        ['prefillFrequency', 'frequency'],
        ['prefillFirstname', 'firstname'],
        ['prefillLastname', 'lastname'],
        ['prefillOrganizationname', 'organizationname'],
        ['prefillEmail', 'email'],
        ['prefillPhone', 'phone'],
        ['prefillAddress', 'address'],
        ['prefillAddress2', 'address2'],
        ['prefillCity', 'city'],
        ['prefillState', 'state'],
        ['prefillZip', 'zip'],
        ['prefillCountry', 'country'],
      ];

      prefillFields.forEach(([formKey, paramKey]) => {
        const val = formData[formKey] as string;
        if (val && val.trim()) {
          prefillParams[paramKey] = val.trim();
        }
      });

      // UTM params (stored alongside prefill params)
      const utmFields: [keyof URLFormData, string][] = [
        ['utmSource', 'utm_source'],
        ['utmMedium', 'utm_medium'],
        ['utmCampaign', 'utm_campaign'],
        ['utmTerm', 'utm_term'],
        ['utmContent', 'utm_content'],
      ];

      utmFields.forEach(([formKey, paramKey]) => {
        const val = formData[formKey] as string;
        if (val && val.trim()) {
          prefillParams[paramKey] = val.trim();
        }
      });

      // Sponsorship params (PRODUCT forms) — use names for case-insensitive matching
      if (selectedSponsorshipGroup) {
        prefillParams['sponsorshipgroup'] = selectedSponsorshipGroup;
      }
      if (selectedSponsorshipProduct) {
        const product = selectedGroupProducts.find(
          (p) => p.productID === selectedSponsorshipProduct,
        );
        if (product) {
          prefillParams['sponsorshipitem'] = product.productName;
        }
      }

      // Custom input params (keyed by ci_<title> or gci_<title> for collisions)
      const saveParamNameMap = buildParamNameMap(formCustomInputs, merchantGlobalCustomInputs);
      Object.entries(customInputValues).forEach(([id, val]) => {
        if (val && val.trim()) {
          const paramName = saveParamNameMap.get(id) || `ci_${id}`;
          prefillParams[paramName] = val.trim();
        }
      });

      // Scheduled payment config override (JSON blob — consumed by donation-elements via elementPrefills)
      if (formHasScheduledPayments && Object.keys(scheduleRanges).length > 0) {
        prefillParams['defaultScheduledRangeOverride'] = JSON.stringify(scheduleRanges);
      }

      // Ticket/seat params (for event forms — HOSTED_ATTEND, OVERLAY_ATTEND)
      if (selectedTicketId) {
        const ticket = formTickets.find((t) => t.id === selectedTicketId);
        if (ticket) {
          prefillParams['ticket'] = ticket.name;
          if (shouldPersistURLBuilderTicketSeats(ticket, seatAttendees.length)) {
            prefillParams['ticketseats'] = String(seatAttendees.length);
          }
        }
        seatAttendees.forEach((attendee, i) => {
          const seatNum = i + 1;
          if (attendee.firstName) prefillParams[`seat${seatNum}_firstname`] = attendee.firstName;
          if (attendee.lastName) prefillParams[`seat${seatNum}_lastname`] = attendee.lastName;
          if (attendee.email) prefillParams[`seat${seatNum}_email`] = attendee.email;
          // Per-seat custom inputs
          Object.entries(attendee.customInputs).forEach(([inputId, val]) => {
            if (val.trim()) {
              const input = formCustomInputs.find((ci) => ci.id === inputId);
              const paramName = input?.title
                ? `seat${seatNum}_ci_${normalizeParamTitle(input.title)}`
                : `seat${seatNum}_ci_${inputId}`;
              prefillParams[paramName] = val;
            }
          });
        });
      }

      const isOverlayType =
        formData.destinationType === 'OVERLAY_DONATE' ||
        formData.destinationType === 'OVERLAY_ATTEND';

      // Get brand color from the selected form
      const selectedForm = formData.connectedForms[0]
        ? filteredForms.find((f) => f.formName === formData.connectedForms[0])
        : null;

      const payload: any = {
        nonprofitName: props.merchant?.merchantName || 'Unknown Merchant',
        CSMerchantID: props.merchant?.organizationId || '',
        elementName: formData.elementName,
        elementType: 'ELEMENT_URL',
        destinationType: formData.destinationType,
        formNames: formData.connectedForms,
        formIDs: formData.connectedForms
          .map((formName) => filteredForms.find((form) => form.formName === formName)?.formID)
          .filter((id) => id !== undefined),
        prefillParams,
        generatedURL: generateURL(),
        active: publicationStateForSave({ existingId: elementID, active, paymentsReady }),
        color: selectedForm?.lightColor || '',
        // Contact ID for restoring selected contact card in edit mode (not a URL param)
        contactID: selectedContact?.contactID || '',
        ...(!elementID && {
          timeCreated: new Date().toISOString(),
          createdBy: getUserEmail() || 'unknown',
        }),
        ...(isOverlayType && { overlayPath: formData.overlayPath.trim() }),
      };

      if (elementID) {
        payload.elementID = elementID;
        delete payload.active;
      }

      const method = elementID ? 'PATCH' : 'POST';
      const url = `${import.meta.env.VITE_APP_ELEMENTS_URL}/elements`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: await getAuthToken(),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        try {
          const data = await response.json();
          toast.error(data.error);
        } catch {
          throw new Error('API request failed');
        }
        return;
      }

      const data = await response.json();

      // On first create the POST stored an empty `generatedURL` (because we
      // didn't yet have an elementID to put in the URL). Follow up with a
      // PATCH that fills it in. We surface PATCH failures so the merchant
      // knows the row exists but its URL is broken — silent failure here
      // would leave anything reading `generatedURL` (copy buttons, exports,
      // emails, sharing cards) stuck on the empty value until the next save.
      let patchFailed = false;
      if (!elementID && data.elementID) {
        setActive(data.active ?? payload.active);
        setElementID(data.elementID);
        try {
          const patchResponse = await fetch(`${import.meta.env.VITE_APP_ELEMENTS_URL}/elements`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: await getAuthToken(),
            },
            body: JSON.stringify({
              elementID: data.elementID,
              nonprofitName: props.merchant?.merchantName || 'Unknown Merchant',
              generatedURL: generateURLWithElementID(data.elementID),
            }),
          });
          if (!patchResponse.ok) {
            patchFailed = true;
            console.warn('Failed to update generatedURL after create:', patchResponse.status);
          }
        } catch (e) {
          patchFailed = true;
          console.warn('Failed to update generatedURL after create:', e);
        }
      }

      if (patchFailed) {
        // Skip onSuccess so the drawer stays open. setElementID has already
        // run, so clicking Save again will hit the PATCH path with a proper
        // generatedURL (since elementID is now in state).
        toast.error('Element created but the generated URL could not be saved. Please save again.');
      } else {
        toast.success(
          elementID
            ? 'Element updated successfully'
            : paymentsReady
              ? 'Element published successfully'
              : 'Draft saved successfully',
        );
        if (props.onSuccess) props.onSuccess(data);
      }
    } catch (error: any) {
      console.error('Submission error:', error);
      toast.error(`Failed to ${elementID ? 'update' : 'publish'} element`, {
        description: error?.message || 'Please try again',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Activate/Deactivate element
  const activateElement = async (): Promise<void> => {
    setIsActivating(true);
    const state = !active;
    if (state && !paymentsReady) {
      setIsActivating(false);
      toast.info('Enable payments before publishing this draft.');
      return;
    }
    const successAlertMessage = state ? 'Element Activated' : 'Element Deactivated';

    try {
      const payload = {
        elementID,
        nonprofitName: props.merchant?.merchantName || 'Unknown Merchant',
        active: state,
      };

      const response = await fetch(
        `${import.meta.env.VITE_APP_ELEMENTS_URL}/elements/toggle-active`,
        {
          method: 'PATCH',
          headers: {
            'content-type': 'application/json',
            Authorization: await getAuthToken(),
          },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      if (!response.ok || data.error) {
        toast.error(data.error || 'Failed to update element status');
        return;
      } else {
        toast.success(successAlertMessage);
        setActive(state);
        if (props.onSuccess) props.onSuccess(data);
      }
    } catch (error) {
      console.error('Activation error:', error);
      toast.error('Failed to update element status');
    } finally {
      setIsActivating(false);
    }
  };

  // Delete element (ELEMENT_URL only)
  const deleteElement = async (): Promise<void> => {
    setIsDeleting(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_APP_ELEMENTS_URL}/elements`, {
        method: 'DELETE',
        headers: {
          'content-type': 'application/json',
          Authorization: await getAuthToken(),
        },
        body: JSON.stringify({
          elementID,
          elementType: 'ELEMENT_URL',
        }),
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        toast.error(data.error || 'Failed to delete element');
        return;
      }

      toast.success('Element deleted');
      if (props.onSuccess) props.onSuccess({ elementID, deleted: true });
      props.setOpen(false);
      clearStates();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete element');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Helper to determine if destination is event-based
  const isEventDestination = (destType: DestinationType): boolean =>
    destType === 'HOSTED_PAGE_ATTEND' || destType === 'OVERLAY_ATTEND';

  // All forms available — grouped by type in the select via <optgroup>
  const filteredForms = availableForms;

  // Get the selected form's configuration
  const selectedFormConfig = formData.connectedForms[0]
    ? filteredForms.find((f) => f.formName === formData.connectedForms[0])
    : null;

  // Whether the selected form has fixed amounts (Sponsorships / PRODUCT type only)
  const isFixedAmountForm = selectedFormConfig?.fundraiserType === 'PRODUCT';

  // Whether the selected form is a crowdfunding form with giving levels
  const isCrowdfundingForm = selectedFormConfig?.formType === 'CROWDFUNDING_FORM';

  // Crowdfunding giving levels for the currently selected frequency
  const crowdfundingOptionsForFrequency = useMemo((): CrowdfundingOption[] => {
    if (!isCrowdfundingForm || !selectedFormConfig) return [];
    const freq = formData.prefillFrequency;
    const freqOptionsMap: Record<string, CrowdfundingOption[] | undefined> = {
      ONE_TIME: selectedFormConfig.oneTimeCrowdfundingOptions,
      DAILY: selectedFormConfig.dailyCrowdfundingOptions,
      WEEKLY: selectedFormConfig.weeklyCrowdfundingOptions,
      MONTHLY: selectedFormConfig.monthlyCrowdfundingOptions,
      ANNUALLY: selectedFormConfig.annuallyCrowdfundingOptions,
    };
    // If a frequency is selected, use that frequency's options; otherwise fall back to legacy
    const options = freq ? freqOptionsMap[freq] : selectedFormConfig.oneTimeCrowdfundingOptions;
    if (options && options.length > 0) return options;
    // Fall back to legacy crowdfundingOptions
    return selectedFormConfig.crowdfundingOptions || [];
  }, [isCrowdfundingForm, selectedFormConfig, formData.prefillFrequency]);

  // Map backend frequency values to display labels
  const FREQUENCY_LABEL_MAP: Record<string, string> = {
    ONE_TIME: 'One-Time',
    DAILY: 'Daily',
    WEEKLY: 'Weekly',
    MONTHLY: 'Monthly',
    ANNUALLY: 'Annually',
  };

  // Available frequencies from the selected form's config (filtered to form's enabled frequencies)
  const formFrequencyOptions = selectedFormConfig?.frequencies?.length
    ? [
        { value: '', label: '' },
        ...selectedFormConfig.frequencies.map((freq) => ({
          value: freq,
          label: FREQUENCY_LABEL_MAP[freq] || freq,
        })),
      ]
    : FREQUENCY_OPTIONS;

  // --- Scheduled payment helpers ---

  // Recurring frequencies the selected form supports (display labels, excluding One-Time)
  const formRecurringFrequencies: RecurringFrequency[] = useMemo(
    () =>
      (selectedFormConfig?.frequencies || [])
        .filter((f) => f !== 'ONE_TIME')
        .map((f) => BACKEND_TO_DISPLAY_FREQ[f])
        .filter(Boolean),
    [selectedFormConfig?.frequencies],
  );

  // Whether the selected form has scheduled payments enabled
  const formHasScheduledPayments =
    !!selectedFormConfig?.requireScheduleDonations && formRecurringFrequencies.length > 0;

  // Map frequency display label → time unit for offset labels
  const getTimeUnitForFrequency = (freq: RecurringFrequency): string => {
    switch (freq) {
      case 'Daily':
        return 'day';
      case 'Weekly':
        return 'week';
      case 'Monthly':
        return 'month';
      case 'Annually':
        return 'year';
      default:
        return 'month';
    }
  };

  const formatTimeUnit = (freq: RecurringFrequency, num: number): string => {
    const unit = getTimeUnitForFrequency(freq);
    return num === 1 ? unit : `${unit}s`;
  };

  const getTimeOptions = (): string[] =>
    Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);

  // Update a single field in the schedule config for a frequency
  const updateScheduleField = (
    freq: RecurringFrequency,
    field: keyof ScheduledRangeConfig,
    value: string | number | boolean | null,
  ) => {
    setScheduleRanges((prev) => ({
      ...prev,
      [freq]: { ...prev[freq]!, [field]: value },
    }));
  };

  // Update multiple fields at once (avoids race conditions between type + value resets)
  const updateScheduleMultipleFields = (
    freq: RecurringFrequency,
    fields: Partial<ScheduledRangeConfig>,
  ) => {
    setScheduleRanges((prev) => ({
      ...prev,
      [freq]: { ...prev[freq]!, ...fields },
    }));
  };

  // Initialize schedule ranges from form defaults when form changes
  // Skip if edit-mode restore already loaded saved config for this form
  useEffect(() => {
    if (!selectedFormConfig) return; // Don't clear ref — edit restore may have set it
    const formId = selectedFormConfig.formID;
    if (scheduleInitializedForFormRef.current === formId) return;
    scheduleInitializedForFormRef.current = formId;

    if (selectedFormConfig.requireScheduleDonations) {
      const ranges = normalizeScheduledRange(
        selectedFormConfig.defaultScheduledRange,
        formRecurringFrequencies,
      );
      setScheduleRanges(ranges);
      if (formRecurringFrequencies.length > 0) {
        setActiveScheduleTab(formRecurringFrequencies[0]);
      }
    } else {
      setScheduleRanges({});
    }
  }, [selectedFormConfig, formRecurringFrequencies]);

  // Available funds from the selected form's config
  const formFunds = selectedFormConfig?.funds || [];

  // Custom inputs from the selected form's config — ensure each has an id (older records may lack one)
  const formCustomInputs = useMemo(
    () =>
      (selectedFormConfig?.customInputs || []).map((ci: FormCustomInput, idx: number) =>
        ci.id ? ci : { ...ci, id: `form_${ci.title?.toLowerCase().replace(/\s+/g, '_') || idx}` },
      ),
    [selectedFormConfig],
  );

  // Merchant-level global custom inputs — ensure each has an id (older records may lack one)
  const merchantGlobalCustomInputs = useMemo(
    () =>
      ((props.merchant?.globalCustomInputs || []) as FormCustomInput[]).map((ci, idx) =>
        ci.id ? ci : { ...ci, id: `global_${ci.title?.toLowerCase().replace(/\s+/g, '_') || idx}` },
      ),
    [props.merchant?.globalCustomInputs],
  );

  // Event form: tickets
  const isEventForm = isEventDestination(formData.destinationType);

  // For events: split custom inputs into attendee (per-seat) and purchaser (global) types
  const attendeeCustomInputs = formCustomInputs.filter(
    (ci) => ci.questionType === 'ATTENDEE' || ci.inputType === 'attendee',
  );
  const nonAttendeeCustomInputs = isEventForm
    ? formCustomInputs.filter((ci) => ci.questionType !== 'ATTENDEE' && ci.inputType !== 'attendee')
    : formCustomInputs;

  // Sponsorship (PRODUCT) form: product groups and products
  const formProductGroups = useMemo(
    () => selectedFormConfig?.productGroups || [],
    [selectedFormConfig?.productGroups],
  );
  const selectedGroupProducts =
    formProductGroups.find((g) => g.productGroupName === selectedSponsorshipGroup)?.products || [];
  const formTickets = selectedFormConfig?.tickets || [];
  const selectedTicket = formTickets.find((t) => t.id === selectedTicketId) || null;
  const seatCount =
    selectedTicket?.groupSizeMode === 'MINIMUM'
      ? seatAttendees.length
      : getURLBuilderConfiguredSeatCount(selectedTicket);

  // Fetch forms
  useEffect(() => {
    const fetchForms = async (): Promise<void> => {
      if (!props.open) return;
      setFormsLoading(true);

      try {
        const response = await fetch(import.meta.env.VITE_APP_FORMS_URL, {
          method: 'GET',
          headers: {
            accept: '*/*',
            'content-type': 'application/json',
            Authorization: await getAuthToken(),
          },
        });

        if (!response.ok) {
          console.error('Error fetching forms:', response.status);
          setAvailableForms([]);
          return;
        }
        const data = await response.json();
        setAvailableForms(
          data.map((form: any) => ({
            formName: form.formName,
            formID: form.formID || form.elementID,
            formType: form.formType,
            fundraiserType: form.fundraiserType,
            funds: form.funds,
            frequencies: form.frequencies,
            customInputs: form.customInputs,
            productGroups: form.productGroups,
            tickets: form.tickets,
            lightColor: form.lightColor,
            oneTimeCrowdfundingOptions: form.oneTimeCrowdfundingOptions,
            dailyCrowdfundingOptions: form.dailyCrowdfundingOptions,
            weeklyCrowdfundingOptions: form.weeklyCrowdfundingOptions,
            monthlyCrowdfundingOptions: form.monthlyCrowdfundingOptions,
            annuallyCrowdfundingOptions: form.annuallyCrowdfundingOptions,
            crowdfundingOptions: form.crowdfundingOptions,
            requireScheduleDonations:
              form.showScheduleDonation || form.requireScheduleDonations || false,
            defaultScheduledRange: form.defaultScheduledRange,
          })),
        );
      } catch (error) {
        console.error('Error fetching forms:', error);
        setAvailableForms([]);
      } finally {
        setFormsLoading(false);
      }
    };

    fetchForms();
  }, [props.open]);

  // Populate form data for edit mode
  useEffect(() => {
    if (props.elementData && props.open) {
      const data = props.elementData;

      setElementID(data.elementID);

      const prefill = data.prefillParams || {};
      setFormData({
        elementName: data.elementName,
        destinationType: data.destinationType || 'HOSTED_PAGE_DONATE',
        connectedForms: data.formNames || [],
        formIDs: data.formIDs || [],
        prefillAmount: prefill.amount || '',
        prefillFund: prefill.fund || '',
        prefillFrequency: prefill.frequency || '',
        prefillFirstname: prefill.firstname || '',
        prefillLastname: prefill.lastname || '',
        prefillOrganizationname: prefill.organizationname || '',
        prefillEmail: prefill.email || '',
        prefillPhone: prefill.phone || '',
        prefillAddress: prefill.address || '',
        prefillAddress2: prefill.address2 || '',
        prefillCity: prefill.city || '',
        prefillState: prefill.state || '',
        prefillZip: prefill.zip || '',
        prefillCountry: prefill.country || '',
        overlayPath: data.overlayPath || '',
        utmSource: prefill.utm_source || '',
        utmMedium: prefill.utm_medium || '',
        utmCampaign: prefill.utm_campaign || '',
        utmTerm: prefill.utm_term || '',
        utmContent: prefill.utm_content || '',
      });
      setActive(data.active);
      setCurrentStep('configuration');

      // Restore sponsorship group directly (stored as name)
      if (prefill.sponsorshipgroup) {
        setSelectedSponsorshipGroup(prefill.sponsorshipgroup);
      }

      // Restore schedule config from saved JSON
      if (prefill.defaultScheduledRangeOverride) {
        try {
          const parsed = JSON.parse(
            prefill.defaultScheduledRangeOverride,
          ) as FrequencyScheduledRangeMap;
          setScheduleRanges(parsed);
          scheduleInitializedForFormRef.current = data.formIDs?.[0] || null;
          const firstFreq = Object.keys(parsed)[0];
          if (firstFreq) setActiveScheduleTab(firstFreq);
        } catch {
          // Invalid JSON — ignore, will fall back to form defaults
        }
      }
    }
  }, [props.elementData, props.open]);

  // Close crowdfunding dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        crowdfundingDropdownRef.current &&
        !crowdfundingDropdownRef.current.contains(e.target as Node)
      ) {
        setCrowdfundingDropdownOpen(false);
      }
    };
    if (crowdfundingDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [crowdfundingDropdownOpen]);

  // Reset form state when opening in create mode (no elementData)
  useEffect(() => {
    if (props.open && !props.elementData) {
      setFormData(initialFormData);
      setElementID(null);
      setActive(true);
      setCurrentStep('configuration');
      setCustomInputValues({});
      setSelectedSponsorshipGroup('');
      setSelectedSponsorshipProduct('');

      setSelectedTicketId('');
      setSeatAttendees([]);
      setContactSearchTerm('');
      setSelectedContact(null);
      resetCustomInputRestoreState();
      setScheduleRanges({});
      setActiveScheduleTab('');
      scheduleInitializedForFormRef.current = null;
    }
  }, [props.open, props.elementData]);

  // Restore sponsorship product from saved name once product groups are available
  useEffect(() => {
    if (!props.elementData || !props.open) return;
    const prefill = props.elementData.prefillParams || {};
    if (!prefill.sponsorshipitem || !prefill.sponsorshipgroup) return;
    if (formProductGroups.length === 0) return;

    const group = formProductGroups.find((g) => g.productGroupName === prefill.sponsorshipgroup);
    if (!group) return;

    const product = group.products.find(
      (p) => p.productName.toLowerCase() === prefill.sponsorshipitem.toLowerCase(),
    );
    if (product) {
      setSelectedSponsorshipProduct(product.productID);
    }
  }, [props.elementData, props.open, formProductGroups]);

  // Restore custom input values from prefillParams when editing an existing element
  // Merges incrementally so form-level and merchant-level inputs are both restored
  // even when they load at different times (merchant globals load from props, form inputs load async)
  useEffect(() => {
    if (!props.elementData || !props.open) {
      resetCustomInputRestoreState();
      return;
    }

    if (formCustomInputs.length === 0 && merchantGlobalCustomInputs.length === 0) return;

    const prefill = props.elementData.prefillParams || {};
    const restored: Record<string, string> = {};

    // Use collision-free param name map to restore values correctly
    const restoreParamMap = buildParamNameMap(formCustomInputs, merchantGlobalCustomInputs);
    restoreParamMap.forEach((paramName, inputId) => {
      if (
        restoredCustomInputIdsRef.current.has(inputId) ||
        touchedCustomInputIdsRef.current.has(inputId)
      ) {
        return;
      }

      // Try the collision-free name first, then fall back to plain ci_ for legacy data
      if (prefill[paramName]) {
        restored[inputId] = prefill[paramName];
      } else if (paramName.startsWith('gci_')) {
        // Legacy data might have been saved with ci_ before this fix
        const legacyName = paramName.replace(/^gci_/, 'ci_');
        if (prefill[legacyName]) {
          restored[inputId] = prefill[legacyName];
        }
      }
    });

    if (Object.keys(restored).length > 0) {
      setCustomInputValues((prev) => {
        const hasChanges = Object.entries(restored).some(([k, v]) => prev[k] !== v);
        return hasChanges ? { ...prev, ...restored } : prev;
      });
      Object.keys(restored).forEach((inputId) => restoredCustomInputIdsRef.current.add(inputId));
    }
  }, [props.elementData, props.open, formCustomInputs, merchantGlobalCustomInputs]);

  // Restore ticket/seat data from saved prefillParams when editing an existing element
  useEffect(() => {
    if (!props.elementData || !props.open) return;
    if (!isEventDestination(formData.destinationType)) return;
    if (formTickets.length === 0) return;

    const prefill = props.elementData.prefillParams || {};
    if (!prefill.ticket) return;

    // Restore selected ticket by name — exact first, as the buyer page resolves it
    const matchedTicket = findURLBuilderTicketByName(formTickets, prefill.ticket);
    if (!matchedTicket) return;
    setSelectedTicketId(matchedTicket.id);

    // Restore seat attendees
    const requestedSeatCount = getURLBuilderSavedSeatCount(matchedTicket, prefill);
    const restoredAttendees: SeatAttendee[] = restoreURLBuilderSeatAttendees(
      prefill,
      requestedSeatCount,
      formCustomInputs,
    );

    if (restoredAttendees.length > 0) {
      setSeatAttendees(restoredAttendees);
    }
  }, [props.elementData, props.open, formTickets, formCustomInputs, formData.destinationType]);

  // Restore selected contact card when editing an element with a saved contactID
  useEffect(() => {
    if (!props.elementData || !props.open) return;
    const savedContactID = props.elementData.contactID;
    if (!savedContactID) return;

    const fetchContact = async () => {
      try {
        const contactResponse = await fetch(
          `${searchReportingLegacyUrl}/validate/contact?contactID=${savedContactID}`,
          {
            method: 'GET',
            headers: {
              accept: '*/*',
              'content-type': 'application/json',
              Authorization: await getAuthToken(),
            },
          },
        );
        if (contactResponse.ok) {
          const responseData = await contactResponse.json();
          const contactData = responseData.contactInformation;
          if (contactData) {
            const fullName =
              contactData.fullName ||
              `${contactData.firstName || ''} ${contactData.lastName || ''}`.trim();
            const email =
              contactData.emails?.filter((e: any) => !e.deleted)?.[0]?.email ||
              contactData.email ||
              '';
            setSelectedContact({
              contactID: savedContactID,
              fullName,
              email,
              organizationName: contactData.organizationName || '',
            });
          }
        }
      } catch (error) {
        // Fallback: reconstruct from saved prefill fields so the card still shows
        const prefill = props.elementData?.prefillParams || {};
        const fullName = `${prefill.firstname || ''} ${prefill.lastname || ''}`.trim();
        if (fullName || prefill.email) {
          setSelectedContact({
            contactID: savedContactID,
            fullName: fullName || 'Unknown Name',
            email: prefill.email || '',
            organizationName: prefill.organizationname || '',
          });
        }
      }
    };

    fetchContact();
  }, [props.elementData, props.open]);

  // Debounce contact search term
  useEffect(() => {
    if (contactSearchTerm.length < 2) {
      setDebouncedSearchTerm('');
      setContactSearchResults([]);
      setShowContactDropdown(false);
      return;
    }
    const timer = setTimeout(() => setDebouncedSearchTerm(contactSearchTerm), 500);
    return () => clearTimeout(timer);
  }, [contactSearchTerm]);

  // Search contacts when debounced term changes
  useEffect(() => {
    if (!debouncedSearchTerm || debouncedSearchTerm.length < 2) return;

    const searchContacts = async () => {
      setIsSearchingContacts(true);
      try {
        const results = await queryContactsTopContacts(
          null, // jwtToken - handled internally by getAuthToken
          [props.merchant?.merchantName || ''],
          '', // amountAtLeast
          '', // amountNoMoreThan
          '', // amountSpecific
          debouncedSearchTerm,
        );
        setContactSearchResults(results);
        setShowContactDropdown(true);
      } catch (error) {
        console.error('Contact search error:', error);
        setContactSearchResults([]);
      } finally {
        setIsSearchingContacts(false);
      }
    };

    searchContacts();
  }, [debouncedSearchTerm, props.merchant?.merchantName]);

  // Close contact dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contactSearchRef.current && !contactSearchRef.current.contains(event.target as Node)) {
        setShowContactDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Convert country name/code to the ISO code used by the country dropdown
  const resolveCountryCode = (country: string): string => {
    if (!country) return '';
    // Already an ISO code (2 letters)?
    const upperCountry = country.toUpperCase();
    if (
      upperCountry.length === 2 &&
      countries.find((c: { value: string }) => c.value === upperCountry)
    ) {
      return upperCountry;
    }
    // Match by label (full name)
    const match = countries.find(
      (c: { value: string; label: string }) => c.label.toLowerCase() === country.toLowerCase(),
    );
    return match?.value || '';
  };

  // Helper to populate form fields from contact data
  const populateContactFields = (contactData: any) => {
    const email =
      contactData.emails?.filter((e: any) => !e.deleted)?.[0]?.email || contactData.email || '';
    const contactFullName = contactData.fullName || contactData.name || '';
    const firstName = contactData.firstName || contactFullName.split(' ')[0] || '';
    const lastName = contactData.lastName || contactFullName.split(' ').slice(1).join(' ') || '';

    // Extract structured address — match ContactInformation.tsx pattern:
    // Priority: is_primary address > first non-deleted address > empty object
    const validAddresses = contactData.addresses?.filter((a: any) => !a.deleted) || [];
    const primaryAddress = validAddresses.find((a: any) => a.is_primary) || validAddresses[0] || {};

    const validPhones = contactData.phones?.filter((p: any) => !p.deleted) || [];
    const primaryPhone =
      validPhones.find((p: any) => p.is_primary)?.value ||
      validPhones[0]?.value ||
      contactData.phone ||
      '';

    // Parse flat address string as fallback (search results return "street, city, state zip, country")
    const parsedAddress = { line1: '', city: '', state: '', zip: '', country: '' };
    if (
      !primaryAddress.line1 &&
      contactData.address &&
      contactData.address !== 'None None, United States'
    ) {
      const parts = contactData.address.split(',').map((p: string) => p.trim());
      if (parts.length >= 3) {
        parsedAddress.line1 = parts[0] || '';
        parsedAddress.city = parts[1] || '';
        // Last part is often country, second-to-last is "STATE ZIP"
        const lastPart = parts[parts.length - 1];
        const secondLast = parts[parts.length - 2] || '';
        // Check if secondLast contains a state+zip like "TX 75081" or "B 08031"
        const stateZipMatch = secondLast.match(/^([A-Za-z]{1,2})\s+(\d{4,5})$/);
        if (stateZipMatch) {
          parsedAddress.state = stateZipMatch[1];
          parsedAddress.zip = stateZipMatch[2];
          parsedAddress.country = lastPart;
        } else {
          // Might be "city, state zip, country" or other formats
          parsedAddress.state = secondLast;
          parsedAddress.country = lastPart;
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      prefillFirstname: firstName || prev.prefillFirstname,
      prefillLastname: lastName || prev.prefillLastname,
      prefillOrganizationname: contactData.organizationName || prev.prefillOrganizationname,
      prefillEmail: email || prev.prefillEmail,
      prefillPhone: primaryPhone || prev.prefillPhone,
      prefillAddress:
        primaryAddress.line1 || contactData.address1 || parsedAddress.line1 || prev.prefillAddress,
      prefillAddress2: primaryAddress.line2 || contactData.address2 || prev.prefillAddress2,
      prefillCity:
        primaryAddress.city || contactData.city || parsedAddress.city || prev.prefillCity,
      prefillState:
        primaryAddress.region ||
        contactData.region ||
        contactData.state ||
        parsedAddress.state ||
        prev.prefillState,
      prefillZip:
        primaryAddress.postal ||
        contactData.zip ||
        contactData.postalCode ||
        parsedAddress.zip ||
        prev.prefillZip,
      prefillCountry:
        resolveCountryCode(
          primaryAddress.country || contactData.country || parsedAddress.country,
        ) || prev.prefillCountry,
    }));

    setSelectedContact((prev: any) => ({
      ...(prev || {}),
      fullName: `${firstName} ${lastName}`.trim() || contactFullName,
      email,
      organizationName: contactData.organizationName || '',
    }));
  };

  // Handle selecting a contact from search results
  const handleContactSelect = async (contact: any) => {
    // Clear search UI immediately
    setContactSearchTerm('');
    setContactSearchResults([]);
    setShowContactDropdown(false);

    // Store contactID so it can be persisted and restored on edit
    const contactId = contact.contactID || contact.id;
    setSelectedContact({ contactID: contactId || null });

    // Populate with search result data first (instant feedback)
    populateContactFields(contact);

    // Fetch full structured contact data (has separate address fields)
    if (contactId) {
      try {
        const contactResponse = await fetch(
          `${searchReportingLegacyUrl}/validate/contact?contactID=${contactId}`,
          {
            method: 'GET',
            headers: {
              accept: '*/*',
              'content-type': 'application/json',
              Authorization: await getAuthToken(),
            },
          },
        );

        if (contactResponse.ok) {
          const responseData = await contactResponse.json();
          const contactData = responseData.contactInformation;
          if (contactData) {
            populateContactFields(contactData);
          }
        }
      } catch (error) {
        console.error('Error fetching full contact details:', error);
        // Already populated with search result data above, so graceful fallback
      }
    }
  };

  const clearStates = (): void => {
    setFormData(initialFormData);
    setElementID(null);
    setAvailableForms([]);
    setFormsLoading(false);
    setInvalidFields(new Set());
    setCurrentStep('configuration');
    setActive(true);
    setIsSaving(false);
    setIsActivating(false);
    setIsDeleting(false);
    setShowDeleteConfirm(false);
    setCustomInputValues({});
    resetCustomInputRestoreState();
    setSelectedSponsorshipGroup('');
    setSelectedSponsorshipProduct('');
    setSelectedTicketId('');
    setSeatAttendees([]);
    setContactSearchTerm('');
    setDebouncedSearchTerm('');
    setContactSearchResults([]);
    setIsSearchingContacts(false);
    setShowContactDropdown(false);
    setSelectedContact(null);
    setScheduleRanges({});
    setActiveScheduleTab('');
    setCrowdfundingDropdownOpen(false);
    scheduleInitializedForFormRef.current = null;

    if (formRef.current) formRef.current.reset();
  };

  const handleSubmitLatest = useLatestCallback(handleSubmit);
  const activateElementLatest = useLatestCallback(activateElement);

  useEffect(() => {
    if (props.presentation !== 'workspace' || !props.setWorkspaceActions) return undefined;

    const actions: WorkspaceHeaderAction[] = [];

    if (elementID) {
      actions.push(
        {
          key: 'save',
          label: isSaving ? 'Saving...' : 'Save',
          variant: 'primary' as const,
          disabled: isSaving || isActivating,
          onClick: () => handleSubmitLatest(),
        },
        {
          key: 'active',
          label: !active
            ? isActivating
              ? 'Activating...'
              : 'Activate'
            : isActivating
              ? 'Deactivating...'
              : 'Deactivate',
          disabled: isActivating || isSaving || (!active && !paymentsReady),
          variant: !active ? 'primary' : 'danger',
          onClick: () => activateElementLatest(),
        },
        {
          key: 'delete',
          label: 'Delete',
          variant: 'danger' as const,
          disabled: isDeleting || isSaving || isActivating,
          onClick: () => setShowDeleteConfirm(true),
        },
      );
    } else {
      actions.push({
        key: 'publish',
        label: isSaving ? 'Saving...' : paymentsReady ? 'Publish' : 'Save draft',
        disabled: isSaving || isActivating,
        variant: 'primary' as const,
        onClick: () => handleSubmitLatest(),
      });
    }

    props.setWorkspaceActions(actions);
    return () => props.setWorkspaceActions?.(null);
    // Register current button state without depending on recreated handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.presentation,
    props.setWorkspaceActions,
    isSaving,
    isActivating,
    isDeleting,
    elementID,
    active,
    paymentsReady,
    handleSubmitLatest,
    activateElementLatest,
  ]);

  if (!props.merchant) {
    return null;
  }

  const generatedURL = generateURL();
  const isOverlayDestination =
    formData.destinationType === 'OVERLAY_DONATE' || formData.destinationType === 'OVERLAY_ATTEND';

  return (
    <Sheet
      embedded={props.presentation === 'workspace'}
      open={props.open}
      onOpenChange={(open) => {
        props.setOpen(open);
        if (!open) clearStates();
      }}
    >
      <SheetContent
        embedded={props.presentation === 'workspace'}
        side="bottom"
        className={
          props.presentation === 'workspace'
            ? 'min-h-full overflow-visible rounded-none bg-white p-0'
            : 'pointer-events-none h-[95vh] overflow-hidden rounded-t-[10px] bg-white'
        }
        onEscapeKeyDown={() => clearStates()}
        onPointerDownOutside={() => clearStates()}
      >
        {props.presentation !== 'workspace' && (
          <VisuallyHidden>
            <SheetTitle>URL Builder</SheetTitle>
          </VisuallyHidden>
        )}
        <div
          className={`mx-auto w-full max-w-[1600px] ${
            props.presentation === 'workspace' ? 'px-3 py-2 md:py-3' : 'h-full pointer-events-none'
          }`}
        >
          {/* Header */}
          {props.presentation !== 'workspace' && (
            <div className="pointer-events-auto mb-4 flex items-center justify-between rounded-lg bg-white p-0">
              <div className="flex items-start gap-x-[6px]">
                <div className="flex h-[38px] w-[38px] items-center justify-center rounded-[19px] border border-edge bg-paper-inset">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="#6B6458"
                    className="h-5 w-5"
                  >
                    <path
                      fillRule="evenodd"
                      d="M19.902 4.098a3.75 3.75 0 00-5.304 0l-4.5 4.5a3.75 3.75 0 001.035 6.037.75.75 0 01-.646 1.353 5.25 5.25 0 01-1.449-8.45l4.5-4.5a5.25 5.25 0 117.424 7.424l-1.757 1.757a.75.75 0 11-1.06-1.06l1.757-1.757a3.75 3.75 0 000-5.304zm-7.389 4.267a.75.75 0 011-.353 5.25 5.25 0 011.449 8.45l-4.5 4.5a5.25 5.25 0 11-7.424-7.424l1.757-1.757a.75.75 0 111.06 1.06l-1.757 1.757a3.75 3.75 0 105.304 5.304l4.5-4.5a3.75 3.75 0 00-1.035-6.037.75.75 0 01-.354-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-base font-bold text-ink-muted">
                    {elementID ? 'Edit URL' : 'Create URL'}
                  </p>
                  <p className="text-sm text-ink-muted">
                    Generate shareable links for your forms with pre-filled inputs, UTM parameters,
                    and more.
                  </p>
                </div>
              </div>

              <div className="flex flex-row items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    props.setOpen(false);
                    clearStates();
                  }}
                >
                  Close
                </Button>

                {elementID && (
                  <Button
                    onClick={() => handleSubmit()}
                    disabled={isSaving || isActivating}
                    variant="primary"
                    size="workspace"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                )}

                {elementID && (
                  <Button
                    onClick={() => activateElement()}
                    disabled={isActivating || isSaving || (!active && !paymentsReady)}
                    variant={!active ? 'primary' : 'danger'}
                    size="workspace"
                  >
                    {!active
                      ? isActivating
                        ? 'Activating...'
                        : 'Activate'
                      : isActivating
                        ? 'Deactivating...'
                        : 'Deactivate'}
                  </Button>
                )}

                {elementID && (
                  <Button
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isDeleting || isSaving || isActivating}
                    variant="danger"
                    size="workspace"
                  >
                    Delete
                  </Button>
                )}

                {!elementID && (
                  <Button
                    onClick={() => handleSubmit()}
                    variant="primary"
                    size="workspace"
                    disabled={isSaving || isActivating}
                  >
                    {isSaving ? 'Saving...' : paymentsReady ? 'Publish' : 'Save draft'}
                  </Button>
                )}
              </div>
            </div>
          )}

          <ConfirmationModal
            open={showDeleteConfirm}
            setOpen={setShowDeleteConfirm}
            onConfirm={() => deleteElement()}
            title="Delete URL Element"
            description="Are you sure you want to delete this URL element? This cannot be undone."
            confirmLabel="Delete"
            cancelLabel="Cancel"
            variant="danger"
            confirmDisabled={isDeleting}
          />

          {props.presentation !== 'workspace' && (
            <div className="pb-3">
              <Separator className="bg-paper-inset" />
            </div>
          )}

          {/* Main Content - Split Layout */}
          <div
            className={`pointer-events-auto flex w-full flex-col-reverse gap-6 lg:flex-row ${
              props.presentation === 'workspace'
                ? 'overflow-visible'
                : 'h-[calc(100%-80px)] overflow-y-auto pb-8 md:overflow-hidden md:pb-0'
            }`}
          >
            {/* Left Side - URL Preview */}
            <div
              className={`flex flex-col gap-4 pr-2 lg:w-1/2 ${
                useCreateWorkspaceCards
                  ? 'overflow-visible'
                  : 'md:h-full md:min-h-full md:overflow-y-auto'
              }`}
            >
              {elementID && generatedURL && canDistribute ? (
                <>
                  <div className="flex w-full flex-col py-4">
                    {isOverlayDestination ? (
                      <OverlaySnippets
                        variant="url-element"
                        elementID={elementID || ''}
                        formID={formData.formIDs[0] || ''}
                        type={formData.destinationType === 'OVERLAY_ATTEND' ? 'attend' : 'donate'}
                        websiteUrl={props.merchant?.organizationBaseURLs?.[0]}
                        color={selectedFormConfig?.lightColor}
                        pagePath={formData.overlayPath}
                        title="URL Triggers"
                      />
                    ) : (
                      <>
                        <p className="mb-2 text-sm font-medium text-ink-secondary">Generated URL</p>
                        <div className="relative w-full">
                          <div
                            role="textbox"
                            aria-readonly="true"
                            tabIndex={0}
                            className="flex min-h-[3.5rem] w-full items-center cursor-not-allowed rounded-md border border-edge bg-paper-inset px-3 pr-24 py-2 text-xs font-mono break-all select-all"
                            onClick={() => {
                              const sel = window.getSelection();
                              const range = document.createRange();
                              const el = document.getElementById('url-builder-generated-url');
                              if (el) {
                                range.selectNodeContents(el);
                                sel?.removeAllRanges();
                                sel?.addRange(range);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.currentTarget.click();
                              }
                            }}
                          >
                            <span id="url-builder-generated-url">{generatedURL}</span>
                          </div>
                          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                            <button
                              type="button"
                              className="flex items-center justify-center rounded-md p-1.5 text-ink-secondary hover:bg-paper-inset hover:text-ink"
                              onClick={async () => {
                                if (await verifyDistribution())
                                  window.open(generatedURL, '_blank', 'noopener,noreferrer');
                              }}
                              title="Open in New Tab"
                            >
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="flex items-center justify-center rounded-md p-1.5 text-ink-secondary hover:bg-paper-inset hover:text-ink"
                              onClick={async () => {
                                if (!(await verifyDistribution())) return;
                                copyToClipboard(generatedURL);
                                toast.success('URL copied to clipboard');
                              }}
                              title="Copy URL"
                            >
                              <svg
                                width="18"
                                height="19"
                                viewBox="0 0 18 19"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M7.875 3.19409C7.36058 3.19409 6.92546 3.53984 6.79207 4.01271C6.76478 4.10947 6.75 4.21209 6.75 4.31909H11.25C11.25 4.21209 11.2352 4.10947 11.2079 4.01271C11.0745 3.53984 10.6394 3.19409 10.125 3.19409H7.875ZM5.8549 3.32751C6.22133 2.58236 6.98769 2.06909 7.875 2.06909H10.125C11.0123 2.06909 11.7787 2.58236 12.1451 3.32751C12.5183 3.35931 12.8899 3.39672 13.2598 3.43965C14.3829 3.56998 15.1875 4.53451 15.1875 5.63683V15.5691C15.1875 16.8117 14.1801 17.8191 12.9375 17.8191H5.0625C3.81986 17.8191 2.8125 16.8117 2.8125 15.5691V5.63683C2.8125 4.53451 3.61712 3.56998 4.74017 3.43965C5.1101 3.39672 5.48171 3.35931 5.8549 3.32751Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* URL Breakdown */}
                  <p className="text-sm font-medium text-ink-secondary">URL Breakdown</p>
                  <div className="flex w-full flex-col rounded-md border border-edge bg-paper-inset px-4 py-4">
                    <div className="space-y-3">
                      {/* Configuration Section */}
                      {(() => {
                        const configItems: { label: string; value: string }[] = [];
                        configItems.push({
                          label: 'Destination',
                          value: `${
                            DESTINATION_TYPE_OPTIONS.find((o) =>
                              formData.destinationType.startsWith(o.value),
                            )?.label || ''
                          } — ${
                            FORM_TYPE_OPTIONS.find((o) =>
                              formData.destinationType.endsWith(o.value),
                            )?.label || ''
                          }`,
                        });

                        if (isEventForm) {
                          // Event-specific breakdown
                          configItems.push({
                            label: 'Event',
                            value: formData.connectedForms[0] || 'None selected',
                          });
                          if (selectedTicket) {
                            configItems.push({ label: 'Ticket', value: selectedTicket.name });
                            if (selectedTicket.price) {
                              configItems.push({
                                label: 'Price',
                                value: `$${selectedTicket.price}`,
                              });
                            }
                          }
                        } else {
                          // Standard form breakdown
                          configItems.push({
                            label: 'Form',
                            value: formData.connectedForms[0] || 'None selected',
                          });
                          if (formData.prefillAmount) {
                            const matchedLevel = isCrowdfundingForm
                              ? crowdfundingOptionsForFrequency.find(
                                  (opt) => opt.amount === formData.prefillAmount,
                                )
                              : null;
                            configItems.push({
                              label: 'Amount',
                              value: matchedLevel
                                ? `${matchedLevel.title} — $${matchedLevel.amount}`
                                : `$${formData.prefillAmount}`,
                            });
                          }
                          if (selectedSponsorshipGroup)
                            configItems.push({
                              label: 'Sponsorship Group',
                              value: selectedSponsorshipGroup,
                            });
                          if (selectedSponsorshipProduct)
                            configItems.push({
                              label: 'Sponsorship Option',
                              value:
                                selectedGroupProducts.find(
                                  (p) => p.productID === selectedSponsorshipProduct,
                                )?.productName || selectedSponsorshipProduct,
                            });
                          if (
                            isFixedAmountForm &&
                            !formData.prefillAmount &&
                            !selectedSponsorshipProduct
                          )
                            configItems.push({
                              label: 'Amount',
                              value: 'Select a Sponsorship Option to set price',
                            });
                          if (formData.prefillFrequency)
                            configItems.push({
                              label: 'Frequency',
                              value:
                                FREQUENCY_LABEL_MAP[formData.prefillFrequency] ||
                                formData.prefillFrequency,
                            });
                        }

                        // Scheduled payment summary
                        if (formHasScheduledPayments) {
                          const configuredFreqs = formRecurringFrequencies.filter((freq) => {
                            const cfg = scheduleRanges[freq];
                            if (!cfg) return false;
                            return (
                              (cfg.startDateType && cfg.startDateType !== 'none') ||
                              (cfg.endDateType && cfg.endDateType !== 'none')
                            );
                          });
                          if (configuredFreqs.length > 0) {
                            configItems.push({
                              label: 'Schedule',
                              value: configuredFreqs.join(', '),
                            });
                          }
                        }

                        if (formData.prefillFund)
                          configItems.push({ label: 'Fund', value: formData.prefillFund });

                        return (
                          <div>
                            <p className="text-xs font-semibold tracking-wider text-ink-muted mb-1">
                              Configuration
                            </p>
                            <ul className="list-disc pl-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-sm text-ink-muted">
                              {configItems.map((item) => (
                                <li key={item.label}>
                                  <span className="font-medium">{item.label}:</span> {item.value}
                                </li>
                              ))}
                            </ul>
                            {/* Attendee details for event forms */}
                            {isEventForm && seatAttendees.length > 0 && (
                              <div className="mt-2">
                                <p className="text-xs font-semibold tracking-wider text-ink-muted mb-1">
                                  Attendees
                                </p>
                                <div className="space-y-1">
                                  {seatAttendees.map((attendee, i) => {
                                    const parts: string[] = [];
                                    if (attendee.firstName || attendee.lastName) {
                                      parts.push(
                                        `${attendee.firstName || ''} ${
                                          attendee.lastName || ''
                                        }`.trim(),
                                      );
                                    }
                                    if (attendee.email) parts.push(attendee.email);
                                    // Collect filled attendee custom inputs
                                    const filledAttendeeInputs = Object.entries(
                                      attendee.customInputs || {},
                                    )
                                      .filter(([, val]) => val && val.trim() !== '')
                                      .map(([inputId, val]) => {
                                        const input = attendeeCustomInputs.find(
                                          (ci) => ci.id === inputId,
                                        );
                                        return { label: input?.title || inputId, value: val };
                                      });
                                    if (parts.length === 0 && filledAttendeeInputs.length === 0)
                                      return null;
                                    return (
                                      <div key={i} className="text-sm text-ink-muted">
                                        <p className="font-medium pl-4">Seat {i + 1}:</p>
                                        <ul className="list-disc pl-8 gap-y-0">
                                          {(attendee.firstName || attendee.lastName) && (
                                            <li>
                                              <span className="font-medium">Name:</span>{' '}
                                              {`${attendee.firstName || ''} ${
                                                attendee.lastName || ''
                                              }`.trim()}
                                            </li>
                                          )}
                                          {attendee.email && (
                                            <li>
                                              <span className="font-medium">Email:</span>{' '}
                                              {attendee.email}
                                            </li>
                                          )}
                                          {filledAttendeeInputs.map((item) => (
                                            <li key={item.label}>
                                              <span className="font-medium">{item.label}:</span>{' '}
                                              {item.value}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Custom Inputs Section */}
                      {(() => {
                        const filledFormInputs = Object.entries(customInputValues)
                          .filter(
                            ([id, val]) =>
                              val.trim() !== '' && formCustomInputs.some((ci) => ci.id === id),
                          )
                          .map(([id, val]) => {
                            const input = formCustomInputs.find((ci) => ci.id === id);
                            return { label: input?.title || id, value: val };
                          });

                        const filledGlobalInputs = Object.entries(customInputValues)
                          .filter(
                            ([id, val]) =>
                              val.trim() !== '' &&
                              merchantGlobalCustomInputs.some((ci) => ci.id === id),
                          )
                          .map(([id, val]) => {
                            const input = merchantGlobalCustomInputs.find((ci) => ci.id === id);
                            return { label: input?.title || id, value: val };
                          });

                        if (filledFormInputs.length === 0 && filledGlobalInputs.length === 0)
                          return null;

                        if (isEventForm) {
                          // For Event forms, separate purchaser and global custom inputs
                          return (
                            <>
                              {filledFormInputs.length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold tracking-wider text-ink-muted mb-1">
                                    Purchaser Custom Inputs
                                  </p>
                                  <ul className="list-disc pl-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-sm text-ink-muted">
                                    {filledFormInputs.map((item) => (
                                      <li key={item.label}>
                                        <span className="font-medium">{item.label}:</span>{' '}
                                        {item.value}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {filledGlobalInputs.length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold tracking-wider text-ink-muted mb-1">
                                    Global Custom Inputs
                                  </p>
                                  <ul className="list-disc pl-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-sm text-ink-muted">
                                    {filledGlobalInputs.map((item) => (
                                      <li key={item.label}>
                                        <span className="font-medium">{item.label}:</span>{' '}
                                        {item.value}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </>
                          );
                        }

                        // For non-Event forms, separate fundraiser and global custom inputs
                        return (
                          <>
                            {filledFormInputs.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold tracking-wider text-ink-muted mb-1">
                                  Fundraiser Custom Inputs
                                </p>
                                <ul className="list-disc pl-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-sm text-ink-muted">
                                  {filledFormInputs.map((item) => (
                                    <li key={item.label}>
                                      <span className="font-medium">{item.label}:</span>{' '}
                                      {item.value}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {filledGlobalInputs.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold tracking-wider text-ink-muted mb-1">
                                  Global Custom Inputs
                                </p>
                                <ul className="list-disc pl-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-sm text-ink-muted">
                                  {filledGlobalInputs.map((item) => (
                                    <li key={item.label}>
                                      <span className="font-medium">{item.label}:</span>{' '}
                                      {item.value}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </>
                        );
                      })()}

                      {/* Contact Info Section */}
                      {(() => {
                        const contactItems: { label: string; value: string }[] = [];
                        if (formData.prefillFirstname)
                          contactItems.push({
                            label: 'First Name',
                            value: formData.prefillFirstname,
                          });
                        if (formData.prefillLastname)
                          contactItems.push({
                            label: 'Last Name',
                            value: formData.prefillLastname,
                          });
                        if (formData.prefillOrganizationname)
                          contactItems.push({
                            label: 'Organization',
                            value: formData.prefillOrganizationname,
                          });
                        if (formData.prefillEmail)
                          contactItems.push({ label: 'Email', value: formData.prefillEmail });
                        if (formData.prefillPhone)
                          contactItems.push({ label: 'Phone', value: formData.prefillPhone });
                        if (formData.prefillAddress)
                          contactItems.push({ label: 'Address', value: formData.prefillAddress });
                        if (formData.prefillAddress2)
                          contactItems.push({
                            label: 'Address 2',
                            value: formData.prefillAddress2,
                          });
                        if (formData.prefillCity)
                          contactItems.push({ label: 'City', value: formData.prefillCity });
                        if (formData.prefillState)
                          contactItems.push({ label: 'State', value: formData.prefillState });
                        if (formData.prefillZip)
                          contactItems.push({ label: 'Zip', value: formData.prefillZip });
                        if (formData.prefillCountry) {
                          const countryMatch = countries.find(
                            (c: { value: string; label: string }) =>
                              c.value === formData.prefillCountry,
                          );
                          contactItems.push({
                            label: 'Country',
                            value: countryMatch ? countryMatch.label : formData.prefillCountry,
                          });
                        }

                        if (contactItems.length === 0) return null;

                        return (
                          <div>
                            <p className="text-xs font-semibold tracking-wider text-ink-muted mb-1">
                              Contact Info
                            </p>
                            <ul className="list-disc pl-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-sm text-ink-muted">
                              {contactItems.map((item) => (
                                <li key={item.label}>
                                  <span className="font-medium">{item.label}:</span> {item.value}
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      })()}

                      {/* UTM Parameters Section */}
                      {(() => {
                        const utmItems: { label: string; value: string }[] = [];
                        if (formData.utmSource)
                          utmItems.push({ label: 'Source', value: formData.utmSource });
                        if (formData.utmMedium)
                          utmItems.push({ label: 'Medium', value: formData.utmMedium });
                        if (formData.utmCampaign)
                          utmItems.push({ label: 'Campaign', value: formData.utmCampaign });
                        if (formData.utmTerm)
                          utmItems.push({ label: 'Term', value: formData.utmTerm });
                        if (formData.utmContent)
                          utmItems.push({ label: 'Content', value: formData.utmContent });

                        if (utmItems.length === 0) return null;

                        return (
                          <div>
                            <p className="text-xs font-semibold tracking-wider text-ink-muted mb-1">
                              UTM Parameters
                            </p>
                            <ul className="list-disc pl-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-sm text-ink-muted">
                              {utmItems.map((item) => (
                                <li key={item.label}>
                                  <span className="font-medium">{item.label}:</span> {item.value}
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex w-full flex-col items-center justify-center rounded-md border border-edge bg-paper-inset px-4 py-12">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="mb-4 h-16 w-16 text-ink-muted"
                  >
                    <path
                      fillRule="evenodd"
                      d="M19.902 4.098a3.75 3.75 0 00-5.304 0l-4.5 4.5a3.75 3.75 0 001.035 6.037.75.75 0 01-.646 1.353 5.25 5.25 0 01-1.449-8.45l4.5-4.5a5.25 5.25 0 117.424 7.424l-1.757 1.757a.75.75 0 11-1.06-1.06l1.757-1.757a3.75 3.75 0 000-5.304zm-7.389 4.267a.75.75 0 011-.353 5.25 5.25 0 011.449 8.45l-4.5 4.5a5.25 5.25 0 11-7.424-7.424l1.757-1.757a.75.75 0 111.06 1.06l-1.757 1.757a3.75 3.75 0 105.304 5.304l4.5-4.5a3.75 3.75 0 00-1.035-6.037.75.75 0 01-.354-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="mb-1 text-center text-sm font-medium text-ink-muted">URL Preview</p>
                  <p className="text-center text-xs text-ink-muted">
                    {!paymentsReady
                      ? 'Enable payments, then publish to unlock your share URL.'
                      : 'Publish the element and its form to unlock your share URL.'}
                  </p>
                </div>
              )}
            </div>

            {/* Right Side - Configuration */}
            <div
              ref={rightPanelRef}
              className={`compact-editor-controls flex flex-col lg:w-1/2 lg:pl-4 ${
                useCreateWorkspaceCards
                  ? 'min-h-0 overflow-visible pb-0'
                  : 'pb-14 md:min-h-full md:overflow-hidden md:pb-0'
              }`}
            >
              {/* Tabs */}
              <div className={useCreateWorkspaceCards ? 'hidden' : 'border-edge bg-white'}>
                <nav
                  className="-mb-px flex space-x-6 overflow-x-auto border-b pb-2 md:pb-0 pt-2 scrollbar-hide"
                  aria-label="Tabs"
                >
                  {tabs.map((tab) => {
                    const tabHasErrors = Array.from(invalidFields).some((field) =>
                      field.startsWith(tab.step + '-'),
                    );
                    return (
                      <button
                        type="button"
                        key={tab.name}
                        onClick={() => setCurrentStep(tab.step)}
                        className={classNames(
                          currentStep === tab.step
                            ? tabHasErrors
                              ? 'border-red-500 text-red-600'
                              : 'border-ink-secondary text-ink-secondary'
                            : tabHasErrors
                              ? 'border-transparent text-red-500 hover:text-red-600'
                              : 'border-transparent text-ink-muted hover:text-ink-secondary',
                          'whitespace-nowrap px-1 pb-1.5 text-[13px] font-medium border-b-2 border-x-0 border-t-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0',
                        )}
                      >
                        {tab.name}
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              <div
                className={`md:flex-1 ${
                  useCreateWorkspaceCards ? 'space-y-4 overflow-visible' : 'py-4 md:overflow-y-auto'
                }`}
              >
                {/* Configuration Tab */}
                <div
                  className={
                    useCreateWorkspaceCards || currentStep === 'configuration'
                      ? useCreateWorkspaceCards
                        ? ''
                        : 'space-y-3'
                      : 'hidden'
                  }
                >
                  <ElementEditorSection
                    enabled={useCreateWorkspaceCards}
                    title="Configuration"
                    description="Set the element name, destination, connected form, and URL behavior."
                    icon={elementEditorSectionIcons.configuration}
                    hasErrors={elementEditorSectionHasErrors(invalidFields, 'configuration')}
                  >
                    {!useCreateWorkspaceCards && (
                      <SectionHeader title="Configuration" className="mb-0">
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3.5 w-3.5 text-white/60 cursor-help shrink-0" />
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-[220px]">
                              <p className="text-xs">
                                Set the element name, destination type, connected form, and overlay
                                path.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </SectionHeader>
                    )}

                    {/* Element Name */}
                    <div>
                      <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                        Element Name
                      </Label>
                      <FormInput
                        id="url-element-name"
                        value={formData.elementName}
                        onChange={(e) => updateFormData('elementName', e.target.value)}
                        onFocus={() => handleFieldFocus('configuration-elementName')}
                        className={`h-7 ${isFieldInvalid('elementName') ? 'border-red-500' : ''}`}
                        placeholder="e.g., Summer Campaign Link"
                      />
                    </div>

                    {/* Destination Type */}
                    <div className="space-y-1">
                      <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                        Destination Type
                      </Label>
                      <div className="grid grid-cols-2 gap-2">
                        {DESTINATION_TYPE_OPTIONS.map((option) => {
                          const disabled = option.value === 'OVERLAY' && !getMerchantDomain();
                          const currentDestType = formData.destinationType.startsWith('OVERLAY')
                            ? 'OVERLAY'
                            : 'HOSTED_PAGE';
                          const isSelected = currentDestType === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              disabled={disabled}
                              onClick={() => {
                                if (disabled) return;
                                const currentFormType = isEventDestination(formData.destinationType)
                                  ? 'ATTEND'
                                  : 'DONATE';
                                const newValue =
                                  `${option.value}_${currentFormType}` as DestinationType;
                                updateFormData('destinationType', newValue);
                              }}
                              className={`flex flex-col items-start rounded-md border p-2.5 text-left transition-colors ${
                                isSelected
                                  ? 'border-sage bg-sage-light'
                                  : 'border-edge bg-white hover:border-edge-input'
                              } ${disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}
                            >
                              <span
                                className={`text-sm font-medium ${isSelected ? 'text-sage-hover' : 'text-ink-secondary'}`}
                              >
                                {option.label}
                              </span>
                              <span className="text-xs text-ink-muted leading-tight mt-0.5">
                                {option.value === 'HOSTED_PAGE'
                                  ? 'A CharityStack-hosted page'
                                  : 'Opens on your website'}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      {!getMerchantDomain() && (
                        <p
                          className={`text-xs ${
                            invalidFields.has('configuration-destinationType')
                              ? 'text-red-500'
                              : 'text-ink-muted'
                          }`}
                        >
                          Overlay options require a Website URL. Configure it in{' '}
                          <button
                            type="button"
                            onClick={() => {
                              props.setOpen(false);
                              history.push('/settings');
                            }}
                            className="text-ink-secondary underline hover:text-ink"
                          >
                            Settings &rarr; Organization
                          </button>
                          .
                        </p>
                      )}
                    </div>

                    {/* Overlay Page Path — shown only for overlay destination types when domain is configured */}
                    {(formData.destinationType === 'OVERLAY_DONATE' ||
                      formData.destinationType === 'OVERLAY_ATTEND') &&
                      getMerchantDomain() && (
                        <PagePathInput
                          domain={getMerchantDomain()}
                          value={formData.overlayPath}
                          onChange={(val) => updateFormData('overlayPath', val)}
                        />
                      )}

                    {/* Connected Form */}
                    <div>
                      <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                        Form
                      </Label>
                      {formsLoading ? (
                        <div className="h-7 w-full animate-pulse rounded-md bg-paper-inset" />
                      ) : (
                        <FormSelect
                          value={formData.connectedForms[0] || ''}
                          onChange={(e) => {
                            const selectedName = e.target.value;
                            updateFormData('connectedForms', selectedName ? [selectedName] : []);
                            updateFormData(
                              'formIDs',
                              selectedName
                                ? [
                                    filteredForms.find((form) => form.formName === selectedName)
                                      ?.formID || '',
                                  ]
                                : [],
                            );
                            handleFieldFocus('configuration-connectedForms');

                            // Auto-set destination suffix based on the selected form's type
                            const newForm = selectedName
                              ? filteredForms.find((f) => f.formName === selectedName)
                              : null;
                            if (newForm) {
                              const destPrefix = formData.destinationType.startsWith('OVERLAY')
                                ? 'OVERLAY'
                                : 'HOSTED_PAGE';
                              const formSuffix = newForm.formType === 'EVENT' ? 'ATTEND' : 'DONATE';
                              const newDestType = `${destPrefix}_${formSuffix}` as DestinationType;
                              if (newDestType !== formData.destinationType) {
                                updateFormData('destinationType', newDestType);
                              }
                            }

                            // Reset dynamic pre-fill fields when form changes
                            if (newForm?.fundraiserType === 'PRODUCT') {
                              updateFormData('prefillAmount', '');
                            }
                            if (newForm?.frequencies?.length && formData.prefillFrequency) {
                              if (!newForm.frequencies.includes(formData.prefillFrequency)) {
                                updateFormData('prefillFrequency', '');
                              }
                            }
                            if (newForm?.funds?.length && formData.prefillFund) {
                              if (!newForm.funds.includes(formData.prefillFund)) {
                                updateFormData('prefillFund', '');
                              }
                            }
                            setCustomInputValues({});
                            resetCustomInputRestoreState();
                            setSelectedSponsorshipGroup('');
                            setSelectedSponsorshipProduct('');
                            setScheduleRanges({});
                            setActiveScheduleTab('');
                            scheduleInitializedForFormRef.current = null;

                            setSelectedTicketId('');
                            setSeatAttendees([]);
                          }}
                          onFocus={() => handleFieldFocus('configuration-connectedForms')}
                          className={`h-7 ${
                            isFieldInvalid('configuration-connectedForms') ? 'border-red-500' : ''
                          }`}
                        >
                          <option value="">Select a form</option>
                          {(() => {
                            const fundraiserForms = (filteredForms || [])
                              .filter((f) => f.formType !== 'EVENT')
                              .sort((a, b) => a.formName.localeCompare(b.formName));
                            const eventForms = (filteredForms || [])
                              .filter((f) => f.formType === 'EVENT')
                              .sort((a, b) => a.formName.localeCompare(b.formName));
                            return (
                              <>
                                {fundraiserForms.length > 0 && (
                                  <optgroup label="Fundraisers">
                                    {fundraiserForms.map((form) => (
                                      <option key={form.formID} value={form.formName}>
                                        {form.formName}
                                      </option>
                                    ))}
                                  </optgroup>
                                )}
                                {eventForms.length > 0 && (
                                  <optgroup label="Events">
                                    {eventForms.map((form) => (
                                      <option key={form.formID} value={form.formName}>
                                        {form.formName}
                                      </option>
                                    ))}
                                  </optgroup>
                                )}
                              </>
                            );
                          })()}
                        </FormSelect>
                      )}
                    </div>

                    {/* Pre-fill Parameters Section — only shown when a form is selected */}
                    {formData.connectedForms.length > 0 && (
                      <>
                        <SectionHeader title="Pre-fill Parameters" className="mt-4 mb-0">
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-3.5 w-3.5 text-white/60 cursor-help shrink-0" />
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-[240px]">
                                <p className="text-xs">
                                  Set default values that will be pre-filled when donors open this
                                  link.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </SectionHeader>

                        <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                          {/* Fund — only for fundraiser destinations, dropdown when form has funds configured */}
                          {!isEventDestination(formData.destinationType) && (
                            <div className="col-span-2">
                              <Label
                                htmlFor="prefill-fund"
                                className="block font-inter text-sm font-medium leading-5 text-ink-muted"
                              >
                                Fund
                              </Label>
                              {formFunds.length > 0 ? (
                                <FormSelect
                                  id="prefill-fund"
                                  value={formData.prefillFund}
                                  onChange={(e) => updateFormData('prefillFund', e.target.value)}
                                  className="h-7"
                                >
                                  <option value=""></option>
                                  {formFunds
                                    .sort((a, b) => a.localeCompare(b))
                                    .map((fund) => (
                                      <option key={fund} value={fund}>
                                        {fund}
                                      </option>
                                    ))}
                                </FormSelect>
                              ) : (
                                <FormInput
                                  id="prefill-fund"
                                  value={formData.prefillFund}
                                  onChange={(e) => updateFormData('prefillFund', e.target.value)}
                                  className="h-7"
                                  placeholder="e.g., General Fund"
                                />
                              )}
                            </div>
                          )}

                          {/* Ticket Type — only for EVENT forms */}
                          {isEventForm && formTickets.length > 0 && (
                            <div className="col-span-2">
                              <Label
                                htmlFor="prefill-ticket"
                                className="block font-inter text-sm font-medium leading-5 text-ink-muted"
                              >
                                Ticket Type
                              </Label>
                              <FormSelect
                                id="prefill-ticket"
                                value={selectedTicketId}
                                onChange={(e) => {
                                  const ticketId = e.target.value;
                                  setSelectedTicketId(ticketId);
                                  const ticket = formTickets.find((t) => t.id === ticketId);
                                  const initialSeats =
                                    ticket?.groupSizeMode === 'MINIMUM'
                                      ? Number(ticket.minimumSeats || 0)
                                      : getURLBuilderConfiguredSeatCount(ticket);
                                  if (ticket && initialSeats > 0) {
                                    setSeatAttendees(
                                      Array.from({ length: initialSeats }, () => ({
                                        firstName: '',
                                        lastName: '',
                                        email: '',
                                        customInputs: {},
                                      })),
                                    );
                                  } else {
                                    setSeatAttendees([]);
                                  }
                                }}
                                className="h-7"
                              >
                                <option value="">Select Ticket Type</option>
                                {formTickets.map((ticket) => {
                                  const remaining =
                                    ticket.quantity != null && ticket.sold != null
                                      ? ticket.quantity - ticket.sold
                                      : null;
                                  const isSoldOut = remaining != null && remaining <= 0;
                                  const lacksMinimumSeats =
                                    ticket.groupSizeMode === 'MINIMUM' &&
                                    Number(ticket.seatCapacity || 0) -
                                      Number(ticket.seatsSold || 0) <
                                      Number(ticket.minimumSeats || 0);
                                  return (
                                    <option
                                      key={ticket.id}
                                      value={ticket.id}
                                      disabled={isSoldOut || lacksMinimumSeats}
                                    >
                                      {ticket.name} - $
                                      {parseFloat(String(ticket.price || 0)).toFixed(2)}
                                      {ticket.groupSizeMode === 'MINIMUM'
                                        ? ` (${ticket.minimumSeats}${
                                            ticket.maximumSeats == null
                                              ? '+'
                                              : `–${ticket.maximumSeats}`
                                          } seats)`
                                        : getURLBuilderConfiguredSeatCount(ticket) > 1
                                          ? ` (${getURLBuilderConfiguredSeatCount(ticket)} seats)`
                                          : ''}
                                      {isSoldOut || lacksMinimumSeats
                                        ? ' - SOLD OUT'
                                        : remaining != null && remaining <= 5
                                          ? ` (${remaining} left)`
                                          : ''}
                                    </option>
                                  );
                                })}
                              </FormSelect>
                            </div>
                          )}

                          {isEventForm &&
                            selectedTicket?.groupSizeMode === 'MINIMUM' &&
                            seatCount > 0 && (
                              <div className="col-span-2">
                                <Label
                                  htmlFor="prefill-ticket-seats"
                                  className="block font-inter text-sm font-medium leading-5 text-ink-muted"
                                >
                                  Group Seats
                                </Label>
                                <Input
                                  id="prefill-ticket-seats"
                                  type="number"
                                  min={selectedTicket.minimumSeats}
                                  max={
                                    selectedTicket.maximumSeats ??
                                    Math.max(
                                      Number(selectedTicket.seatCapacity || 0) -
                                        Number(selectedTicket.seatsSold || 0),
                                      0,
                                    )
                                  }
                                  value={seatAttendees.length}
                                  onChange={(event) => {
                                    const nextCount = Number(event.target.value);
                                    const minimum = Number(selectedTicket.minimumSeats || 0);
                                    const capacityMaximum = Math.max(
                                      Number(selectedTicket.seatCapacity || 0) -
                                        Number(selectedTicket.seatsSold || 0),
                                      0,
                                    );
                                    const maximum = Math.min(
                                      selectedTicket.maximumSeats == null
                                        ? capacityMaximum
                                        : Number(selectedTicket.maximumSeats),
                                      capacityMaximum,
                                    );
                                    if (
                                      !Number.isInteger(nextCount) ||
                                      nextCount < minimum ||
                                      nextCount > maximum
                                    ) {
                                      return;
                                    }
                                    setSeatAttendees((current) =>
                                      Array.from(
                                        { length: nextCount },
                                        (_, index) =>
                                          current[index] || {
                                            firstName: '',
                                            lastName: '',
                                            email: '',
                                            customInputs: {},
                                          },
                                      ),
                                    );
                                  }}
                                  className="h-7"
                                />
                              </div>
                            )}

                          {/* Seat Attendees — shown when a ticket with seats is selected */}
                          {isEventForm && selectedTicket && seatCount > 0 && (
                            <>
                              {seatAttendees.map((attendee, index) => (
                                <div
                                  key={index}
                                  className="col-span-2 space-y-1 rounded-md border border-edge bg-paper-inset p-2"
                                >
                                  <p className="text-xs font-medium text-ink-muted">
                                    Seat {index + 1}
                                  </p>
                                  <div className="grid grid-cols-3 gap-2">
                                    <div className="space-y-0">
                                      <Label className="text-xs text-ink-secondary">
                                        First Name
                                      </Label>
                                      <Input
                                        value={attendee.firstName}
                                        onChange={(e) => {
                                          const updated = [...seatAttendees];
                                          updated[index] = {
                                            ...updated[index],
                                            firstName: e.target.value,
                                          };
                                          setSeatAttendees(updated);
                                        }}
                                        className="h-[28px] rounded-md border border-edge-input bg-white"
                                        placeholder="First Name"
                                      />
                                    </div>
                                    <div className="space-y-0">
                                      <Label className="text-xs text-ink-secondary">
                                        Last Name
                                      </Label>
                                      <Input
                                        value={attendee.lastName}
                                        onChange={(e) => {
                                          const updated = [...seatAttendees];
                                          updated[index] = {
                                            ...updated[index],
                                            lastName: e.target.value,
                                          };
                                          setSeatAttendees(updated);
                                        }}
                                        className="h-[28px] rounded-md border border-edge-input bg-white"
                                        placeholder="Last Name"
                                      />
                                    </div>
                                    <div className="space-y-0">
                                      <Label className="text-xs text-ink-secondary">Email</Label>
                                      <Input
                                        value={attendee.email}
                                        onChange={(e) => {
                                          const updated = [...seatAttendees];
                                          updated[index] = {
                                            ...updated[index],
                                            email: e.target.value,
                                          };
                                          setSeatAttendees(updated);
                                        }}
                                        className="h-[28px] rounded-md border border-edge-input bg-white"
                                        placeholder="Email"
                                      />
                                    </div>
                                  </div>
                                  {/* Per-seat custom inputs (ATTENDEE-type questions) */}
                                  {attendeeCustomInputs.length > 0 && (
                                    <div className="mt-1 grid grid-cols-1 gap-2">
                                      {attendeeCustomInputs.map((input, idx) => (
                                        <div
                                          key={input.id || `attendee-ci-${idx}`}
                                          className="space-y-0"
                                        >
                                          <Label className="text-xs text-ink-secondary">
                                            {input.title}
                                            {input.required && (
                                              <span className="text-gray-400 ml-0.5">*</span>
                                            )}
                                          </Label>
                                          {input.type === 'MULTI_SELECTION_DROPDOWN' ? (
                                            <MultiSelectDropdownNoCreate
                                              options={(input.options || []).map((opt: string) => ({
                                                value: opt,
                                                label: opt,
                                              }))}
                                              items={(attendee.customInputs[input.id] || '')
                                                .split(',')
                                                .filter(Boolean)}
                                              setItems={(values: string[]) => {
                                                const updated = [...seatAttendees];
                                                updated[index] = {
                                                  ...updated[index],
                                                  customInputs: {
                                                    ...updated[index].customInputs,
                                                    [input.id]: values.join(','),
                                                  },
                                                };
                                                setSeatAttendees(updated);
                                              }}
                                              height={28}
                                            />
                                          ) : input.type === 'SINGLE_SELECTION_DROPDOWN' ? (
                                            <FormSelect
                                              value={attendee.customInputs[input.id] || ''}
                                              onChange={(e) => {
                                                const updated = [...seatAttendees];
                                                updated[index] = {
                                                  ...updated[index],
                                                  customInputs: {
                                                    ...updated[index].customInputs,
                                                    [input.id]: e.target.value,
                                                  },
                                                };
                                                setSeatAttendees(updated);
                                              }}
                                              className="h-7"
                                            >
                                              <option value=""></option>
                                              {(input.options || []).map(
                                                (opt: string, optIdx: number) => (
                                                  <option key={`${opt}-${optIdx}`} value={opt}>
                                                    {opt}
                                                  </option>
                                                ),
                                              )}
                                            </FormSelect>
                                          ) : input.type === 'LONG_TEXT_INPUT' ? (
                                            <textarea
                                              value={attendee.customInputs[input.id] || ''}
                                              onChange={(e) => {
                                                const updated = [...seatAttendees];
                                                updated[index] = {
                                                  ...updated[index],
                                                  customInputs: {
                                                    ...updated[index].customInputs,
                                                    [input.id]: e.target.value,
                                                  },
                                                };
                                                setSeatAttendees(updated);
                                              }}
                                              rows={3}
                                              className="w-full rounded-md border border-edge-input bg-white px-2 py-1.5 text-sm resize-none outline-none focus:ring-2 focus:ring-sage focus:border-sage"
                                              placeholder={input.title}
                                            />
                                          ) : (
                                            <input
                                              type="text"
                                              value={attendee.customInputs[input.id] || ''}
                                              onChange={(e) => {
                                                const updated = [...seatAttendees];
                                                updated[index] = {
                                                  ...updated[index],
                                                  customInputs: {
                                                    ...updated[index].customInputs,
                                                    [input.id]: e.target.value,
                                                  },
                                                };
                                                setSeatAttendees(updated);
                                              }}
                                              className="h-7 w-full rounded-md border border-edge-input bg-white px-2 py-0 text-sm leading-tight outline-none focus:ring-2 focus:ring-sage focus:border-sage"
                                              placeholder={input.title}
                                            />
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </>
                          )}

                          {/* Sponsorship Group — only for PRODUCT forms (Row 1, Col 1) */}
                          {isFixedAmountForm && formProductGroups.length > 0 && (
                            <div className="space-y-0">
                              <Label
                                htmlFor="prefill-sponsorship-group"
                                className="text-sm font-medium text-ink-secondary"
                              >
                                Sponsorship Group
                              </Label>
                              <FormSelect
                                id="prefill-sponsorship-group"
                                value={selectedSponsorshipGroup}
                                onChange={(e) => {
                                  setSelectedSponsorshipGroup(e.target.value);
                                  setSelectedSponsorshipProduct('');
                                  updateFormData('prefillAmount', '');
                                }}
                                className="h-7"
                              >
                                <option value="">Select Group</option>
                                {formProductGroups.map((group) => (
                                  <option
                                    key={group.productGroupName}
                                    value={group.productGroupName}
                                  >
                                    {group.productGroupName}
                                  </option>
                                ))}
                              </FormSelect>
                            </div>
                          )}

                          {/* Sponsorship Option — always visible for PRODUCT forms, disabled until group selected (Row 1, Col 2) */}
                          {isFixedAmountForm && formProductGroups.length > 0 && (
                            <div className="space-y-0">
                              <Label
                                htmlFor="prefill-sponsorship-option"
                                className="text-sm font-medium text-ink-secondary"
                              >
                                Sponsorship Option
                              </Label>
                              <FormSelect
                                id="prefill-sponsorship-option"
                                value={selectedSponsorshipProduct}
                                onChange={(e) => {
                                  const productValue = e.target.value;
                                  setSelectedSponsorshipProduct(productValue);
                                  // Auto-fill amount from product price
                                  if (productValue) {
                                    const product = selectedGroupProducts.find(
                                      (p) => p.productID === productValue,
                                    );
                                    if (product) {
                                      updateFormData('prefillAmount', String(product.productPrice));
                                    }
                                  } else {
                                    updateFormData('prefillAmount', '');
                                  }
                                }}
                                disabled={!selectedSponsorshipGroup}
                                className={`h-7 ${
                                  !selectedSponsorshipGroup
                                    ? 'cursor-not-allowed text-ink-muted'
                                    : ''
                                }`}
                              >
                                <option value="">
                                  {selectedSponsorshipGroup
                                    ? 'Select Option'
                                    : 'Select a group first'}
                                </option>
                                {selectedGroupProducts.map((product) => (
                                  <option key={product.productID} value={product.productID}>
                                    {product.productName} — ${product.productPrice}
                                  </option>
                                ))}
                              </FormSelect>
                            </div>
                          )}

                          {/* Amount — hidden for events, disabled for Sponsorships (PRODUCT) */}
                          {!isEventForm && (
                            <div className="space-y-0">
                              <Label
                                htmlFor="prefill-amount"
                                className="text-sm font-medium text-ink-secondary"
                              >
                                Amount
                              </Label>
                              {isCrowdfundingForm && crowdfundingOptionsForFrequency.length > 0 ? (
                                <div ref={crowdfundingDropdownRef} className="relative w-full">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setCrowdfundingDropdownOpen(!crowdfundingDropdownOpen)
                                    }
                                    className={`relative flex h-7 min-h-7 w-full items-center justify-between rounded-md border border-edge bg-white px-2 text-left text-sm leading-tight hover:border-edge focus:border-edge focus:outline-none ${
                                      formData.prefillAmount
                                        ? 'text-ink-secondary'
                                        : 'text-ink-muted'
                                    }`}
                                  >
                                    <span className="block truncate">
                                      {formData.prefillAmount
                                        ? crowdfundingOptionsForFrequency.find(
                                            (opt) => opt.amount === formData.prefillAmount,
                                          )
                                          ? `${
                                              crowdfundingOptionsForFrequency.find(
                                                (opt) => opt.amount === formData.prefillAmount,
                                              )!.title
                                            } — $${formData.prefillAmount}`
                                          : `$${formData.prefillAmount}`
                                        : 'Select or enter amount'}
                                    </span>
                                    {formData.prefillAmount ? (
                                      <X
                                        className="h-3.5 w-3.5 shrink-0 text-ink-muted hover:text-ink-secondary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          updateFormData('prefillAmount', '');
                                        }}
                                      />
                                    ) : (
                                      <ChevronDown className="h-4 w-4 shrink-0 text-ink-muted" />
                                    )}
                                  </button>
                                  {crowdfundingDropdownOpen && (
                                    <div className="absolute z-10 mt-1 w-full rounded-md border border-edge bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
                                      <div className="py-1">
                                        {crowdfundingOptionsForFrequency.map((opt) => (
                                          <button
                                            key={opt.id}
                                            type="button"
                                            onClick={() => {
                                              updateFormData('prefillAmount', opt.amount);
                                              setCrowdfundingDropdownOpen(false);
                                            }}
                                            className={`w-full px-3 py-2 text-left text-sm ${
                                              opt.amount === formData.prefillAmount
                                                ? 'bg-ink-secondary text-white'
                                                : 'text-ink-secondary hover:bg-paper-inset hover:text-ink'
                                            }`}
                                          >
                                            {opt.title} — ${opt.amount}
                                          </button>
                                        ))}
                                        <div className="border-t border-edge px-3 py-2">
                                          <input
                                            type="number"
                                            placeholder="Custom amount"
                                            className="h-7 w-full rounded-md border border-edge-input bg-white px-2 text-sm text-ink-secondary focus:border-edge focus:outline-none"
                                            value={
                                              crowdfundingOptionsForFrequency.some(
                                                (opt) => opt.amount === formData.prefillAmount,
                                              )
                                                ? ''
                                                : formData.prefillAmount
                                            }
                                            onChange={(e) =>
                                              updateFormData('prefillAmount', e.target.value)
                                            }
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter' && formData.prefillAmount) {
                                                setCrowdfundingDropdownOpen(false);
                                              }
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <Input
                                  id="prefill-amount"
                                  type="number"
                                  value={formData.prefillAmount}
                                  onChange={(e) => updateFormData('prefillAmount', e.target.value)}
                                  onFocus={() => handleFieldFocus('configuration-prefillAmount')}
                                  min={1}
                                  max={1_000_000}
                                  className={`h-[28px] rounded-md border ${
                                    isFieldInvalid('prefillAmount')
                                      ? 'border-red-500'
                                      : 'border-edge-input'
                                  } ${
                                    isFixedAmountForm
                                      ? 'cursor-not-allowed bg-white text-ink-muted'
                                      : 'bg-white'
                                  }`}
                                  placeholder={
                                    isFixedAmountForm ? 'Fixed by Sponsorship price' : ''
                                  }
                                  disabled={isFixedAmountForm}
                                />
                              )}
                            </div>
                          )}

                          {/* Frequency — hidden for events */}
                          {!isEventForm && (
                            <div className="space-y-0">
                              <Label
                                htmlFor="prefill-frequency"
                                className="text-sm font-medium text-ink-secondary"
                              >
                                Frequency
                              </Label>
                              <FormSelect
                                id="prefill-frequency"
                                value={formData.prefillFrequency}
                                onChange={(e) => {
                                  updateFormData('prefillFrequency', e.target.value);
                                  // Reset amount selection when frequency changes for crowdfunding (levels differ per frequency)
                                  if (isCrowdfundingForm) {
                                    updateFormData('prefillAmount', '');
                                  }
                                }}
                                className="h-7"
                              >
                                {formFrequencyOptions.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </FormSelect>
                            </div>
                          )}

                          {/* Scheduled Payments — show when form has scheduled payments enabled */}
                          {formHasScheduledPayments && (
                            <div className="col-span-2 space-y-2 rounded-md border border-edge bg-paper-inset p-2">
                              <p className="text-xs font-medium text-ink-muted">
                                Scheduled Payment Defaults
                              </p>

                              {/* Frequency Tabs */}
                              <nav
                                className="-mb-px flex space-x-4 overflow-x-auto border-b pb-1"
                                aria-label="Schedule Frequency Tabs"
                              >
                                {formRecurringFrequencies.map((freq) => (
                                  <button
                                    key={`sched-tab-${freq}`}
                                    type="button"
                                    onClick={() => setActiveScheduleTab(freq)}
                                    className={classNames(
                                      activeScheduleTab === freq
                                        ? 'border-ink-secondary text-ink-secondary'
                                        : 'border-transparent text-ink-muted hover:text-ink-secondary',
                                      'whitespace-nowrap px-1 pb-1.5 text-[13px] font-medium border-b-2 border-x-0 border-t-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0',
                                    )}
                                  >
                                    {freq}
                                  </button>
                                ))}
                              </nav>

                              {/* Tab Content */}
                              {activeScheduleTab &&
                                scheduleRanges[activeScheduleTab as RecurringFrequency] && (
                                  <div className="space-y-2 pt-1">
                                    {/* Default Start Date */}
                                    <div className="space-y-1">
                                      <Label className="text-xs font-medium text-ink-muted">
                                        Default Start Date
                                      </Label>
                                      <div className="grid grid-cols-2 gap-2">
                                        <FormSelect
                                          value={
                                            scheduleRanges[activeScheduleTab as RecurringFrequency]
                                              ?.startDateType || 'none'
                                          }
                                          onChange={(e) => {
                                            const newType = e.target.value as DateConfigType;
                                            if (newType === 'fixed') {
                                              updateScheduleMultipleFields(
                                                activeScheduleTab as RecurringFrequency,
                                                { startDateType: 'fixed', startDateOffset: null },
                                              );
                                            } else if (newType === 'dynamic') {
                                              updateScheduleMultipleFields(
                                                activeScheduleTab as RecurringFrequency,
                                                { startDateType: 'dynamic', fixedStartDate: null },
                                              );
                                            } else {
                                              updateScheduleMultipleFields(
                                                activeScheduleTab as RecurringFrequency,
                                                {
                                                  startDateType: 'none',
                                                  startDateOffset: null,
                                                  fixedStartDate: null,
                                                },
                                              );
                                            }
                                          }}
                                          className="h-7"
                                        >
                                          <option value="none">None</option>
                                          <option value="dynamic">Dynamic</option>
                                          <option value="fixed">Fixed</option>
                                        </FormSelect>

                                        {/* Dynamic or None: offset selector */}
                                        {(scheduleRanges[activeScheduleTab as RecurringFrequency]
                                          ?.startDateType || 'none') !== 'fixed' && (
                                          <select
                                            value={
                                              (scheduleRanges[
                                                activeScheduleTab as RecurringFrequency
                                              ]?.startDateType || 'none') === 'none'
                                                ? ''
                                                : (scheduleRanges[
                                                    activeScheduleTab as RecurringFrequency
                                                  ]?.startDateOffset ?? '')
                                            }
                                            onChange={(e) =>
                                              updateScheduleField(
                                                activeScheduleTab as RecurringFrequency,
                                                'startDateOffset',
                                                e.target.value
                                                  ? parseInt(e.target.value, 10)
                                                  : null,
                                              )
                                            }
                                            disabled={
                                              (scheduleRanges[
                                                activeScheduleTab as RecurringFrequency
                                              ]?.startDateType || 'none') === 'none'
                                            }
                                            className={`h-7 w-full rounded-md border border-edge-input px-2 py-0 text-sm leading-tight ${
                                              (scheduleRanges[
                                                activeScheduleTab as RecurringFrequency
                                              ]?.startDateType || 'none') === 'none'
                                                ? 'bg-paper-inset text-ink-muted cursor-not-allowed'
                                                : 'bg-white text-ink-secondary'
                                            }`}
                                          >
                                            <option value="">-</option>
                                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((num) => (
                                              <option key={num} value={num}>
                                                In {num}{' '}
                                                {formatTimeUnit(
                                                  activeScheduleTab as RecurringFrequency,
                                                  num,
                                                )}
                                              </option>
                                            ))}
                                          </select>
                                        )}

                                        {/* Fixed: date input */}
                                        {scheduleRanges[activeScheduleTab as RecurringFrequency]
                                          ?.startDateType === 'fixed' && (
                                          <input
                                            type="date"
                                            value={
                                              scheduleRanges[
                                                activeScheduleTab as RecurringFrequency
                                              ]?.fixedStartDate || ''
                                            }
                                            min={new Date().toISOString().split('T')[0]}
                                            onChange={(e) =>
                                              updateScheduleField(
                                                activeScheduleTab as RecurringFrequency,
                                                'fixedStartDate',
                                                e.target.value || null,
                                              )
                                            }
                                            className="h-7 w-full rounded-md border border-edge-input bg-white px-2 py-0 text-sm leading-tight text-ink-secondary"
                                          />
                                        )}
                                      </div>
                                    </div>

                                    {/* Default End Date */}
                                    <div className="space-y-1">
                                      <Label className="text-xs font-medium text-ink-muted">
                                        Default End Date
                                      </Label>
                                      <div className="grid grid-cols-2 gap-2">
                                        <select
                                          value={
                                            scheduleRanges[activeScheduleTab as RecurringFrequency]
                                              ?.endDateType || 'none'
                                          }
                                          onChange={(e) => {
                                            const newType = e.target.value as DateConfigType;
                                            if (newType === 'fixed') {
                                              updateScheduleMultipleFields(
                                                activeScheduleTab as RecurringFrequency,
                                                { endDateType: 'fixed', endDateOffset: null },
                                              );
                                            } else if (newType === 'dynamic') {
                                              updateScheduleMultipleFields(
                                                activeScheduleTab as RecurringFrequency,
                                                { endDateType: 'dynamic', fixedEndDate: null },
                                              );
                                            } else {
                                              updateScheduleMultipleFields(
                                                activeScheduleTab as RecurringFrequency,
                                                {
                                                  endDateType: 'none',
                                                  endDateOffset: null,
                                                  fixedEndDate: null,
                                                },
                                              );
                                            }
                                          }}
                                          className="h-7 w-full rounded-md border border-edge-input bg-white px-2 py-0 text-sm leading-tight text-ink-secondary"
                                        >
                                          <option value="none">None</option>
                                          <option value="dynamic">Dynamic</option>
                                          <option value="fixed">Fixed</option>
                                        </select>

                                        {/* Dynamic or None: offset selector */}
                                        {(scheduleRanges[activeScheduleTab as RecurringFrequency]
                                          ?.endDateType || 'none') !== 'fixed' && (
                                          <select
                                            value={
                                              (scheduleRanges[
                                                activeScheduleTab as RecurringFrequency
                                              ]?.endDateType || 'none') === 'none'
                                                ? ''
                                                : (scheduleRanges[
                                                    activeScheduleTab as RecurringFrequency
                                                  ]?.endDateOffset ?? '')
                                            }
                                            onChange={(e) =>
                                              updateScheduleField(
                                                activeScheduleTab as RecurringFrequency,
                                                'endDateOffset',
                                                e.target.value
                                                  ? parseInt(e.target.value, 10)
                                                  : null,
                                              )
                                            }
                                            disabled={
                                              (scheduleRanges[
                                                activeScheduleTab as RecurringFrequency
                                              ]?.endDateType || 'none') === 'none'
                                            }
                                            className={`h-7 w-full rounded-md border border-edge-input px-2 py-0 text-sm leading-tight ${
                                              (scheduleRanges[
                                                activeScheduleTab as RecurringFrequency
                                              ]?.endDateType || 'none') === 'none'
                                                ? 'bg-paper-inset text-ink-muted cursor-not-allowed'
                                                : 'bg-white text-ink-secondary'
                                            }`}
                                          >
                                            <option value="">-</option>
                                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((num) => (
                                              <option key={num} value={num}>
                                                In {num}{' '}
                                                {formatTimeUnit(
                                                  activeScheduleTab as RecurringFrequency,
                                                  num,
                                                )}
                                              </option>
                                            ))}
                                          </select>
                                        )}

                                        {/* Fixed: date input */}
                                        {scheduleRanges[activeScheduleTab as RecurringFrequency]
                                          ?.endDateType === 'fixed' && (
                                          <input
                                            type="date"
                                            value={
                                              scheduleRanges[
                                                activeScheduleTab as RecurringFrequency
                                              ]?.fixedEndDate || ''
                                            }
                                            min={new Date().toISOString().split('T')[0]}
                                            onChange={(e) =>
                                              updateScheduleField(
                                                activeScheduleTab as RecurringFrequency,
                                                'fixedEndDate',
                                                e.target.value || null,
                                              )
                                            }
                                            className="h-7 w-full rounded-md border border-edge-input bg-white px-2 py-0 text-sm leading-tight text-ink-secondary"
                                          />
                                        )}
                                      </div>
                                    </div>

                                    {/* Default Time */}
                                    <div className="space-y-1">
                                      <Label className="text-xs font-medium text-ink-muted">
                                        Default Time (Optional)
                                      </Label>
                                      <select
                                        value={
                                          scheduleRanges[activeScheduleTab as RecurringFrequency]
                                            ?.scheduledTime || ''
                                        }
                                        onChange={(e) =>
                                          updateScheduleField(
                                            activeScheduleTab as RecurringFrequency,
                                            'scheduledTime',
                                            e.target.value,
                                          )
                                        }
                                        className="h-7 w-full rounded-md border border-edge-input bg-white px-2 py-0 text-sm leading-tight text-ink-secondary"
                                      >
                                        <option value="">Select time</option>
                                        {getTimeOptions().map((time) => (
                                          <option key={time} value={time}>
                                            {time}
                                          </option>
                                        ))}
                                      </select>
                                    </div>

                                    {/* Lock Schedule */}
                                    <div className="flex items-center gap-2 border-t border-edge pt-2 mt-1">
                                      <input
                                        type="checkbox"
                                        id={`lock-schedule-${activeScheduleTab}`}
                                        checked={
                                          scheduleRanges[activeScheduleTab as RecurringFrequency]
                                            ?.lockedScheduleDate === true
                                        }
                                        onChange={(e) =>
                                          updateScheduleField(
                                            activeScheduleTab as RecurringFrequency,
                                            'lockedScheduleDate',
                                            e.target.checked,
                                          )
                                        }
                                        className="h-3.5 w-3.5 rounded border-edge-input"
                                      />
                                      <Label
                                        htmlFor={`lock-schedule-${activeScheduleTab}`}
                                        className="text-xs text-ink-muted cursor-pointer"
                                      >
                                        Lock {activeScheduleTab} dates for donors
                                      </Label>
                                      <TooltipProvider delayDuration={200}>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Info className="h-3 w-3 text-ink-muted cursor-help shrink-0" />
                                          </TooltipTrigger>
                                          <TooltipContent side="top" className="max-w-[200px]">
                                            <p className="text-xs">
                                              Donors cannot edit or remove {activeScheduleTab}{' '}
                                              schedule
                                            </p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    </div>
                                  </div>
                                )}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </ElementEditorSection>
                </div>

                {/* Custom Inputs Tab */}
                <div
                  className={
                    useCreateWorkspaceCards || currentStep === 'custom-inputs'
                      ? useCreateWorkspaceCards
                        ? ''
                        : 'space-y-3'
                      : 'hidden'
                  }
                >
                  <ElementEditorSection
                    enabled={useCreateWorkspaceCards}
                    title="Custom Inputs"
                    description="Prefill the form's custom input responses."
                    icon={elementEditorSectionIcons.customInputs}
                    hasErrors={elementEditorSectionHasErrors(invalidFields, 'custom-inputs')}
                  >
                    {/* Merchant Global Custom Inputs */}
                    {merchantGlobalCustomInputs.length > 0 && (
                      <div className="space-y-2">
                        <SectionHeader title="Global Custom Inputs" className="mb-0">
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-3.5 w-3.5 text-white/60 cursor-help shrink-0" />
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-[240px]">
                                <p className="text-xs">
                                  Pre-fill merchant-level custom input values shared across all
                                  forms.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </SectionHeader>
                        <div className="grid grid-cols-1 gap-x-3 gap-y-2">
                          {merchantGlobalCustomInputs.map((input, idx) => (
                            <div key={input.id || `merchant-ci-${idx}`} className="space-y-0">
                              <Label
                                htmlFor={`custom-${input.id}`}
                                className="text-sm font-medium text-ink-secondary"
                              >
                                {input.title}
                                {input.required && <span className="text-gray-400 ml-0.5">*</span>}
                              </Label>
                              {input.type === 'MULTI_SELECTION_DROPDOWN' ? (
                                <MultiSelectDropdownNoCreate
                                  options={(input.options || []).map((opt: string) => ({
                                    value: opt,
                                    label: opt,
                                  }))}
                                  items={(customInputValues[input.id] || '')
                                    .split(',')
                                    .filter(Boolean)}
                                  setItems={(values: string[]) =>
                                    updateCustomInputValue(input.id, values.join(','))
                                  }
                                  height={28}
                                />
                              ) : input.type === 'SINGLE_SELECTION_DROPDOWN' ? (
                                <select
                                  id={`custom-${input.id}`}
                                  value={customInputValues[input.id] || ''}
                                  onChange={(e) => updateCustomInputValue(input.id, e.target.value)}
                                  className="h-7 w-full rounded-md border border-edge-input bg-white px-2 py-0 text-sm leading-tight"
                                >
                                  <option value=""></option>
                                  {(input.options || []).map((opt: string, optIdx: number) => (
                                    <option key={`${opt}-${optIdx}`} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                </select>
                              ) : input.type === 'LONG_TEXT_INPUT' ? (
                                <textarea
                                  id={`custom-${input.id}`}
                                  value={customInputValues[input.id] || ''}
                                  onChange={(e) => updateCustomInputValue(input.id, e.target.value)}
                                  rows={3}
                                  className="w-full rounded-md border border-edge-input bg-white px-2 py-1.5 text-sm resize-none outline-none focus:ring-2 focus:ring-sage focus:border-sage"
                                  placeholder={input.title}
                                />
                              ) : (
                                <input
                                  type="text"
                                  id={`custom-${input.id}`}
                                  value={customInputValues[input.id] || ''}
                                  onChange={(e) => updateCustomInputValue(input.id, e.target.value)}
                                  className="h-7 w-full rounded-md border border-edge-input bg-white px-2 py-0 text-sm leading-tight outline-none focus:ring-2 focus:ring-sage focus:border-sage"
                                  placeholder={input.title}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Separator between merchant and form inputs when both exist */}
                    {merchantGlobalCustomInputs.length > 0 &&
                      nonAttendeeCustomInputs.length > 0 && <Separator />}

                    {/* Form-Level Custom Inputs (non-attendee / purchaser inputs) */}
                    {nonAttendeeCustomInputs.length > 0 && (
                      <div className="space-y-2">
                        <SectionHeader
                          title={
                            isEventForm ? 'Purchaser Custom Inputs' : 'Fundraiser Custom Inputs'
                          }
                          className="mb-0"
                        >
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-3.5 w-3.5 text-white/60 cursor-help shrink-0" />
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-[240px]">
                                <p className="text-xs">
                                  Pre-fill form-level custom input values specific to this form.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </SectionHeader>
                        <div className="grid grid-cols-1 gap-x-3 gap-y-2">
                          {nonAttendeeCustomInputs.map((input, idx) => (
                            <div key={input.id || `form-ci-${idx}`} className="space-y-0">
                              <Label
                                htmlFor={`custom-${input.id}`}
                                className="text-sm font-medium text-ink-secondary"
                              >
                                {input.title}
                                {input.required && <span className="text-gray-400 ml-0.5">*</span>}
                              </Label>
                              {input.type === 'MULTI_SELECTION_DROPDOWN' ? (
                                <MultiSelectDropdownNoCreate
                                  options={(input.options || []).map((opt: string) => ({
                                    value: opt,
                                    label: opt,
                                  }))}
                                  items={(customInputValues[input.id] || '')
                                    .split(',')
                                    .filter(Boolean)}
                                  setItems={(values: string[]) =>
                                    updateCustomInputValue(input.id, values.join(','))
                                  }
                                  height={28}
                                />
                              ) : input.type === 'SINGLE_SELECTION_DROPDOWN' ? (
                                <select
                                  id={`custom-${input.id}`}
                                  value={customInputValues[input.id] || ''}
                                  onChange={(e) => updateCustomInputValue(input.id, e.target.value)}
                                  className="h-7 w-full rounded-md border border-edge-input bg-white px-2 py-0 text-sm leading-tight"
                                >
                                  <option value=""></option>
                                  {(input.options || []).map((opt: string, optIdx: number) => (
                                    <option key={`${opt}-${optIdx}`} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                </select>
                              ) : input.type === 'LONG_TEXT_INPUT' ? (
                                <textarea
                                  id={`custom-${input.id}`}
                                  value={customInputValues[input.id] || ''}
                                  onChange={(e) => updateCustomInputValue(input.id, e.target.value)}
                                  rows={3}
                                  className="w-full rounded-md border border-edge-input bg-white px-2 py-1.5 text-sm resize-none outline-none focus:ring-2 focus:ring-sage focus:border-sage"
                                  placeholder={input.title}
                                />
                              ) : (
                                <input
                                  type="text"
                                  id={`custom-${input.id}`}
                                  value={customInputValues[input.id] || ''}
                                  onChange={(e) => updateCustomInputValue(input.id, e.target.value)}
                                  className="h-7 w-full rounded-md border border-edge-input bg-white px-2 py-0 text-sm leading-tight outline-none focus:ring-2 focus:ring-sage focus:border-sage"
                                  placeholder={input.title}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Empty state when no visible custom inputs exist */}
                    {merchantGlobalCustomInputs.length === 0 &&
                      (isEventForm || nonAttendeeCustomInputs.length === 0) && (
                        <div className="flex flex-col items-center justify-center rounded-md border border-edge bg-paper-inset px-4 py-12">
                          <p className="text-sm text-ink-muted">
                            {formData.connectedForms.length > 0
                              ? 'No custom inputs configured for this form or merchant.'
                              : 'Select a form to see available custom inputs.'}
                          </p>
                        </div>
                      )}
                  </ElementEditorSection>
                </div>

                {/* Contact Info Tab */}
                <div
                  className={
                    useCreateWorkspaceCards || currentStep === 'contact-info'
                      ? useCreateWorkspaceCards
                        ? ''
                        : 'space-y-3'
                      : 'hidden'
                  }
                >
                  <ElementEditorSection
                    enabled={useCreateWorkspaceCards}
                    title="Contact Information"
                    description="Find a contact or prefill donor contact details."
                    icon={elementEditorSectionIcons.contactInformation}
                    hasErrors={elementEditorSectionHasErrors(invalidFields, 'contact-info')}
                  >
                    {!useCreateWorkspaceCards && (
                      <SectionHeader title="Contact Info" className="mb-0">
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3.5 w-3.5 text-white/60 cursor-help shrink-0" />
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-[260px]">
                              <p className="text-xs">
                                Search for an existing contact to auto-fill fields, or type values
                                manually. Selecting a contact does not create or modify any records.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </SectionHeader>
                    )}

                    {/* Contact Search */}
                    {!selectedContact && (
                      <div ref={contactSearchRef} className="relative">
                        <Label
                          htmlFor="contact-search"
                          className="text-sm font-medium text-ink-secondary"
                        >
                          Search Contacts
                        </Label>
                        <div className="relative">
                          <Input
                            id="contact-search"
                            value={contactSearchTerm}
                            onChange={(e) => setContactSearchTerm(e.target.value)}
                            onFocus={() => {
                              if (contactSearchResults.length > 0) setShowContactDropdown(true);
                            }}
                            className="h-[28px] rounded-md border border-edge-input bg-white pr-8"
                            placeholder="Search by name or email (min 2 characters)"
                            autoComplete="off"
                          />
                          {isSearchingContacts && (
                            <div className="absolute right-2 top-1/2 -translate-y-1/2">
                              <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-edge-input border-t-ink-secondary" />
                            </div>
                          )}
                        </div>

                        {/* Search Results Dropdown */}
                        {showContactDropdown && contactSearchResults.length > 0 && (
                          <div className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-edge bg-white shadow-lg">
                            {contactSearchResults.map((contact, index) => {
                              const displayName =
                                contact.fullName ||
                                contact.name ||
                                `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
                              const displayEmail =
                                contact.emails?.[0]?.email || contact.email || '';
                              return (
                                <button
                                  key={contact.id || index}
                                  type="button"
                                  className="flex w-full flex-col px-3 py-2 text-left hover:bg-paper-inset"
                                  onClick={() => handleContactSelect(contact)}
                                >
                                  <span className="text-sm font-medium text-ink">
                                    {displayName || 'Unknown'}
                                  </span>
                                  {displayEmail && (
                                    <span className="text-xs text-ink-muted">{displayEmail}</span>
                                  )}
                                  {contact.organizationName && (
                                    <span className="text-xs text-ink-muted">
                                      {contact.organizationName}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {showContactDropdown &&
                          contactSearchResults.length === 0 &&
                          debouncedSearchTerm.length >= 2 &&
                          !isSearchingContacts && (
                            <div className="absolute z-50 mt-1 w-full rounded-md border border-edge bg-white px-3 py-2 shadow-lg">
                              <span className="text-sm text-ink-muted">No contacts found.</span>
                            </div>
                          )}
                      </div>
                    )}

                    {/* Selected Contact Card */}
                    {selectedContact && (
                      <div className="rounded-md border border-edge bg-paper-muted p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-ink">
                              {selectedContact.fullName || 'Unknown Name'}
                            </div>
                            {selectedContact.email && (
                              <div className="text-sm text-ink-muted">{selectedContact.email}</div>
                            )}
                            {selectedContact.organizationName && (
                              <div className="text-xs text-ink-muted">
                                {selectedContact.organizationName}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedContact(null);
                              setFormData((prev) => ({
                                ...prev,
                                prefillFirstname: '',
                                prefillLastname: '',
                                prefillOrganizationname: '',
                                prefillEmail: '',
                                prefillPhone: '',
                                prefillAddress: '',
                                prefillAddress2: '',
                                prefillCity: '',
                                prefillState: '',
                                prefillZip: '',
                                prefillCountry: '',
                              }));
                            }}
                            className="text-ink-muted hover:text-ink-secondary"
                          >
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}

                    <Separator />

                    {/* Name Fields */}
                    <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                      <div className="space-y-0">
                        <Label
                          htmlFor="prefill-firstname"
                          className="text-sm font-medium text-ink-secondary"
                        >
                          First Name
                        </Label>
                        <Input
                          id="prefill-firstname"
                          value={formData.prefillFirstname}
                          onChange={(e) => updateFormData('prefillFirstname', e.target.value)}
                          className="h-[28px] rounded-md border border-edge-input bg-white"
                        />
                      </div>
                      <div className="space-y-0">
                        <Label
                          htmlFor="prefill-lastname"
                          className="text-sm font-medium text-ink-secondary"
                        >
                          Last Name
                        </Label>
                        <Input
                          id="prefill-lastname"
                          value={formData.prefillLastname}
                          onChange={(e) => updateFormData('prefillLastname', e.target.value)}
                          className="h-[28px] rounded-md border border-edge-input bg-white"
                        />
                      </div>
                    </div>

                    {/* Organization Name - full width */}
                    <div className="space-y-0">
                      <Label
                        htmlFor="prefill-orgname"
                        className="text-sm font-medium text-ink-secondary"
                      >
                        Organization Name
                      </Label>
                      <Input
                        id="prefill-orgname"
                        value={formData.prefillOrganizationname}
                        onChange={(e) => updateFormData('prefillOrganizationname', e.target.value)}
                        className="h-[28px] rounded-md border border-edge-input bg-white"
                      />
                    </div>

                    {/* Email - full width */}
                    <div className="space-y-0">
                      <Label
                        htmlFor="prefill-email"
                        className="text-sm font-medium text-ink-secondary"
                      >
                        Email
                      </Label>
                      <Input
                        id="prefill-email"
                        type="email"
                        value={formData.prefillEmail}
                        onChange={(e) => updateFormData('prefillEmail', e.target.value)}
                        className="h-[28px] rounded-md border border-edge-input bg-white"
                      />
                    </div>

                    {/* Phone - full width */}
                    <div className="space-y-0">
                      <Label
                        htmlFor="prefill-phone"
                        className="text-sm font-medium text-ink-secondary"
                      >
                        Phone
                      </Label>
                      <PhoneInput
                        country={'us'}
                        value={formData.prefillPhone}
                        onChange={(value) => updateFormData('prefillPhone', value)}
                        enableSearch={true}
                        disableSearchIcon={true}
                        buttonStyle={{
                          borderRadius: '6px 0px 0px 6px',
                          border: '1px solid #E0D9CC',
                        }}
                        inputStyle={{
                          width: '100%',
                          height: '28px',
                          backgroundColor: '#F5F3ED',
                          borderRadius: '6px',
                          border: '1px solid #E0D9CC',
                        }}
                        preferredCountries={['us', 'ca']}
                        dropdownStyle={{
                          width: 'full',
                          borderRadius: '6px',
                        }}
                        searchStyle={{
                          width: '92%',
                          borderRadius: '6px',
                        }}
                      />
                    </div>

                    <Separator />

                    {/* Address Section */}
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-ink-secondary">Address</p>

                      {/* Address Line 1 - full width */}
                      <div className="space-y-0">
                        <Label
                          htmlFor="prefill-address"
                          className="text-sm font-medium text-ink-secondary"
                        >
                          Address Line 1
                        </Label>
                        <Input
                          id="prefill-address"
                          value={formData.prefillAddress}
                          onChange={(e) => updateFormData('prefillAddress', e.target.value)}
                          className="h-[28px] rounded-md border border-edge-input bg-white"
                          autoComplete="address-line1"
                        />
                      </div>

                      {/* Address Line 2 - full width */}
                      <div className="space-y-0">
                        <Label
                          htmlFor="prefill-address2"
                          className="text-sm font-medium text-ink-secondary"
                        >
                          Address Line 2
                        </Label>
                        <Input
                          id="prefill-address2"
                          value={formData.prefillAddress2}
                          onChange={(e) => updateFormData('prefillAddress2', e.target.value)}
                          className="h-[28px] rounded-md border border-edge-input bg-white"
                          autoComplete="address-line2"
                        />
                      </div>

                      {/* City / State / Zip - 3-column grid */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-0">
                          <Label
                            htmlFor="prefill-city"
                            className="text-sm font-medium text-ink-secondary"
                          >
                            City
                          </Label>
                          <Input
                            id="prefill-city"
                            value={formData.prefillCity}
                            onChange={(e) => updateFormData('prefillCity', e.target.value)}
                            className="h-[28px] rounded-md border border-edge-input bg-white"
                            autoComplete="address-level2"
                          />
                        </div>
                        <div className="space-y-0">
                          <Label
                            htmlFor="prefill-state"
                            className="text-sm font-medium text-ink-secondary"
                          >
                            State
                          </Label>
                          <Input
                            id="prefill-state"
                            value={formData.prefillState}
                            onChange={(e) => updateFormData('prefillState', e.target.value)}
                            className="h-[28px] rounded-md border border-edge-input bg-white"
                            autoComplete="address-level1"
                          />
                        </div>
                        <div className="space-y-0">
                          <Label
                            htmlFor="prefill-zip"
                            className="text-sm font-medium text-ink-secondary"
                          >
                            Zip Code
                          </Label>
                          <Input
                            id="prefill-zip"
                            value={formData.prefillZip}
                            onChange={(e) => updateFormData('prefillZip', e.target.value)}
                            className="h-[28px] rounded-md border border-edge-input bg-white"
                            autoComplete="postal-code"
                          />
                        </div>
                      </div>

                      {/* Country - select dropdown */}
                      <div className="space-y-0">
                        <Label
                          htmlFor="prefill-country"
                          className="text-sm font-medium text-ink-secondary"
                        >
                          Country
                        </Label>
                        <select
                          id="prefill-country"
                          value={formData.prefillCountry}
                          onChange={(e) => updateFormData('prefillCountry', e.target.value)}
                          className="h-7 w-full rounded-md border border-edge-input bg-white px-2 py-0 text-sm leading-tight"
                          autoComplete="country"
                        >
                          <option value="">Select a country</option>
                          {countries.map((c: { value: string; label: string }) => (
                            <option key={c.value} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </ElementEditorSection>
                </div>

                {/* UTM Parameters Tab */}
                <div
                  className={
                    useCreateWorkspaceCards || currentStep === 'utm'
                      ? useCreateWorkspaceCards
                        ? ''
                        : 'space-y-3'
                      : 'hidden'
                  }
                >
                  <ElementEditorSection
                    enabled={useCreateWorkspaceCards}
                    title="UTM Parameters"
                    description="Add campaign attribution parameters to the generated URL."
                    icon={elementEditorSectionIcons.tracking}
                    hasErrors={elementEditorSectionHasErrors(invalidFields, 'utm')}
                  >
                    {!useCreateWorkspaceCards && (
                      <SectionHeader title="UTM Parameters" className="mb-0">
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3.5 w-3.5 text-white/60 cursor-help shrink-0" />
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-[260px]">
                              <p className="text-xs">
                                Track campaign performance with UTM parameters. These are
                                automatically captured by the existing analytics pipeline.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </SectionHeader>
                    )}

                    <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                      <div className="space-y-0">
                        <Label
                          htmlFor="utm-source"
                          className="text-sm font-medium text-ink-secondary"
                        >
                          Source
                        </Label>
                        <Input
                          id="utm-source"
                          value={formData.utmSource}
                          onChange={(e) => updateFormData('utmSource', e.target.value)}
                          className="h-[28px] rounded-md border border-edge-input bg-white"
                          placeholder="e.g., email, facebook"
                        />
                      </div>

                      <div className="space-y-0">
                        <Label
                          htmlFor="utm-medium"
                          className="text-sm font-medium text-ink-secondary"
                        >
                          Medium
                        </Label>
                        <Input
                          id="utm-medium"
                          value={formData.utmMedium}
                          onChange={(e) => updateFormData('utmMedium', e.target.value)}
                          className="h-[28px] rounded-md border border-edge-input bg-white"
                          placeholder="e.g., cpc, social"
                        />
                      </div>

                      <div className="col-span-2 space-y-0">
                        <Label
                          htmlFor="utm-campaign"
                          className="text-sm font-medium text-ink-secondary"
                        >
                          Campaign
                        </Label>
                        <Input
                          id="utm-campaign"
                          value={formData.utmCampaign}
                          onChange={(e) => updateFormData('utmCampaign', e.target.value)}
                          className="h-[28px] rounded-md border border-edge-input bg-white"
                          placeholder="e.g., ramadan2026, spring-gala"
                        />
                      </div>

                      <div className="space-y-0">
                        <Label
                          htmlFor="utm-term"
                          className="text-sm font-medium text-ink-secondary"
                        >
                          Term
                        </Label>
                        <Input
                          id="utm-term"
                          value={formData.utmTerm}
                          onChange={(e) => updateFormData('utmTerm', e.target.value)}
                          className="h-[28px] rounded-md border border-edge-input bg-white"
                          placeholder="e.g., donate, zakat"
                        />
                      </div>

                      <div className="space-y-0">
                        <Label
                          htmlFor="utm-content"
                          className="text-sm font-medium text-ink-secondary"
                        >
                          Content
                        </Label>
                        <Input
                          id="utm-content"
                          value={formData.utmContent}
                          onChange={(e) => updateFormData('utmContent', e.target.value)}
                          className="h-[28px] rounded-md border border-edge-input bg-white"
                          placeholder="e.g., header-banner"
                        />
                      </div>
                    </div>
                  </ElementEditorSection>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
